import { BrandingImpl } from "../BrandingConfiguration"
import { QrCodeFormat } from "../globalStates/ExhibitorDataTypes"

export interface Branding {
    /**
     * @title App & General Configuration
     * @description Configuration information for the app to be runnable
     * @title_de App & Allgemeine Konfiguration
     * @description_de Konfigurationsinformationen, damit die App überhaupt läuft
     */
    configuration: Configuration
    /**
     * @title Page Title
     * @description Title displayed in browser window for the app
     * @title_de Seitentitel
     * @description_de Titel im Browser-Fenter für die App
     */
    pageTitle: string
    /**
     * @title Application text languages
     * @description Languages in which the application's titles, labels, descriptions and other texts can be displayed
     * @title_de App-Textsprachen
     * @description_de Sprachen, in denen Titel, Beschriftungen, Beschreibungen und andere Texte der App angezeigt werden können
     */
    availableLanguages: string[]
    /**
     * @title Content languages
     * @description Languages in which the information about the company and its products, trademarks, job offers etc. can be entered
     * @title_de Inhaltssprachen
     * @description_de Sprachen, in denen Informationen über das Unternehmen und seine Produkte, Marken, Stellenangebote usw. eingegeben werden können
     */
    contentLanguages: string[]
    /**
     * @title Show limit counter
     * @description Toggle visibility of counter for the added entities
     * @title_de Limitzähler anzeigen
     * @description_de Schalten Sie die Sichtbarkeit des Zählers für die hinzugefügten Entitäten um
     */
    showCounter: boolean
    /**
     * @title Limit counter label
     * @description Template for the limit counter label
     * @title_de Begrenzungszähleretikett
     * @description_de Vorlage für die Begrenzungszähleretikett
     */
    lblLimit: LocalizedString
    /**
     * @title Root categories selectable
     * @description Enable/disable selection of categeries that have child elements
     * @title_de Stammkategorien wählbar
     * @description_de Aktivieren/deaktivieren Sie die Auswahl von Kategorien, die untergeordnete Elemente haben
     */
    rootCategoriesSelectable: boolean
    /**
     * @title Use config data V2
     * @description Toggle usage of new version of shop assets endpoint
     * @title_de Konfigurationsdaten V2 verwenden
     * @description_de Schalten Sie die Verwendung der neuen Version des Shop-Assets-Endpoints um
     */
    useConfigDataV2: boolean
    /**
     * @title Use uppseling for unavailable sections
     * @description Toggle the usage of upselling functionalities for pages/sections without available service keys
     * @title_de Verwenden Sie Upselling für nicht verfügbare Abschnitte
     * @description_de Umschalten der Nutzung von Upselling-Funktionalitäten für Seiten/Abschnitte ohne verfügbare Serviceschlüssel
     */
    useUpselling: boolean
    /**
     * @title Loader bg color
     * @description Background color for the centered loader
     * @title_de Loader bg color
     * @description_de Background color for the centered loader
     */
    loaderBgColor: string
    /**
     * @title Loader spin color
     * @description Top border color for the centered loader
     * @title_de Loader spin color
     * @description_de Top border color for the centered loader
     */
    loaderSpinColor: ColorString
}

export interface Configuration {
    /**
     * @title topic name
     * @description Name of the topic for the data access (e.g. 2021_hh)
     * @title_de topic name
     * @description_de Name des topics für den Datenzugriff (z.b. 2021_hh)
     */
    topicName: string
    /**
     * @title series of topics token
     * @description series of topics token for the data access
     * @title_de series of topics token
     * @description_de series of topics token für den Datenzugriff
     */
    sotAccessToken: string
    /**
     * @title Open Graph Title
     * @description Title for the meta-tag og:title
     * @title_de Open Graph Titel
     * @description_de Titel für das og:title meta-tag
     */
    ogTitle: LocalizedString
    /**
     * @title Open Graph Description
     * @description Title for the meta-tag og:title
     * @title_de Open Graph Beschreibung
     * @description_de Titel für das og:description meta-tag
     */
    ogDescription: LocalizedString
    /**
     * @title Dominant color
     * @description Dominant color in the application
     * @title_de Dominant color
     * @description_de Dominant color in the application
     */
    maincolor: ColorString
    /**
     * @title Days of fair/conference (format: YYYY-MM-dd)
     * @description List of dates on which the fair/conference is happening
     * @title_de Messe-/Konferenztage (Format: YYYY-MM-dd)
     * @description_de Liste der Termine, an denen die Messe/Konferenz stattfindet
     */
    eventDays: string[]
    /**
     * @title Event Time Zone
     * @description Time zone of the the whole event. Usually this will be the timezone that corresponds to the geographic location of the event. Example "Europe/Berlin". Valid time zone names can be found in the column TZ indentifier here: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones.
     * @default Europe/Berlin
     * @title_de Event-Zeitzone
     * @description_de Zeitzone der Veranstaltung. In der Regel entspricht dies der Zeitzone des geografischen Ortes des Events. Beispiel: "Europe/Berlin". Valide Zeitzonen können der Spalte TZ identifier hier entnommen werden: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones.
     */
    eventTimeZone: string
    /**
     * @title Date format
     * @description Picker's date format
     * @title_de Date format
     * @description_de Picker's date format
     */
    dateFormat: string
    /**
     * @title Time format
     * @description Picker's time format
     * @title_de Time format
     * @description_de Picker's time format
     */
    timeFormat: string
    /**
     * @title Categories visibility flag
     * @description Flag to enable the categories' code visibility
     * @title_de Categories visibility flag
     * @description_de Flag to enable the categories' code visibility
     */
    showCategoriesCode: boolean
    /**
     * @title Licences visibility flag
     * @description Flag to enable the licences' code visibility
     * @title_de Lizenzen Sichtbarkeitskennzeichen
     * @description_de Flagge zur Aktivierung der Sichtbarkeit des Lizenzcodes
     */
    showLicencesCode: boolean
    /**
     * @title Searchbar hint
     * @description Hint for the searchbar input field
     * @title_de Searchbar hint
     * @description_de Hint for the searchbar input field
     */
    hntSearch: LocalizedString
    /**
     * @title Chips hint
     * @description Hint for the chips input field
     * @title_de Chips hint
     * @description_de Hint for the chips input field
     */
    hntChips: LocalizedString
    /**
     * @title Comming soon label
     * @description Label for the Comming soon message
     * @title_de Comming soon label
     * @description_de Label for the Comming soon message
     */
    lblCommingSoon: LocalizedString
    /**
     * @title Empty dropdown value label
     * @description Label for the empty dropdown value
     * @title_de Empty dropdown value label
     * @description_de Label for the empty dropdown value
     */
    lblEmptySelect: LocalizedString
    /**
     * @title ModalPublish labels
     * @description Labels for the ModalPublish
     * @title_de ModalPublish labels
     * @description_de Labels for the ModalPublish
     */
    modalPublish: ModalPublishConfiguration
    /**
     * @title Language switch labels
     * @description Labels for the Language switch
     * @title_de Language switch labels
     * @description_de Labels for the Language switch
     */
    languageSwitch: LanguageSwitchConfiguration
    /**
     * @title Actionbar labels
     * @description Labels for the actionbar
     * @title_de Actionbar labels
     * @description_de Labels for the actionbar
     */
    actionbar: ActionbarConfiguration
    /**
     * @title Sidebar configuration
     * @description Configuration of the sidebar navigation
     * @title_de Konfiguration der Sidebar
     * @description_de Konfiguration der Sidebar-Navigation
     */
    sidebar: SidebarConfiguration
    /**
     * @title Event Cloud products configuration
     * @description Configuration for the Event Cloud products list
     * @title_de Konfiguration der Event-Cloud-Produkte
     * @description_de Konfiguration für die Liste der Event-Cloud-Produkte
     */
    eventCloudProducts: EventCloudProductsConfiguration
    /**
     * @title QR codes configuration
     * @description Configuration for the QR code download functionality
     * @title_de Konfiguration der QR-Codes
     * @description_de Konfiguration für die QR-Code-Download-Funktionalität
     */
    qrCodes: QrCodesConfiguration
    /**
     * @title Top bar configuration
     * @description Configuration for the top bar elements
     * @title_de Top-Bar-Konfiguration
     * @description_de Konfiguration für die Top-Bar-Elemente
     */
    topBar: TopBarConfiguration
    /**
     * @title Profile page configuration
     * @description Labels for the Profile page
     * @title_de Konfiguration der Profilseite
     * @description_de Beschriftungen für die Profilseite
     */
    profilePage: ProfilePageConfiguration
    /**
     * @title Links page configuration
     * @description Labels for the Links page
     * @title_de Links Seite konfigurieren
     * @description_de Beschriftungen für die Seite "Links"
     */
    linksPage: LinksPageConfiguration
    /**
     * @title Categories page configuration
     * @description Labels for the Categories page
     * @title_de Konfiguration der Kategorieseite
     * @description_de Beschriftungen für die Seite "Kategorien"
     */
    categoriesPage: CategoriesPageConfiguration
    /**
     * @title Licences page configuration
     * @description Labels for the Licences page
     * @title_de Konfiguration der Lizenzen
     * @description_de Beschriftungen für die Seite "Lizenzen"
     */
    licencesPage: LicencesPageConfiguration
    /**
     * @title Products page configuration
     * @description Labels for the Products page
     * @title_de Konfiguration der Produktseite
     * @description_de Etiketten für die Seite "Produkte"
     */
    productsPage: ProductsPageConfiguration
    /**
     * @title Products for collections page configuration
     * @description Labels for the Products page
     * @title_de Konfiguration der Seite "Produkte für Sammlungen"
     * @description_de Etiketten für die Seite "Produkte für Sammlungen"
     */
    productsForCollectionsPage: ProductsForCollectionsPageConfiguration
    /**
     * @title Trademarks page configuration
     * @description Labels for the Trademarks page
     * @title_de Konfiguration der Markenseite
     * @description_de  Etiketten für die Seite "Marken"
     */
    trademarksPage: TrademarksPageConfiguration
    /**
     * @title Team page configuration
     * @description Labels for the Team page
     * @title_de Konfiguration der Teamseite
     * @description_de Beschriftungen für die Seite "Team"
     */
    teamPage: TeamPageConfiguration
    /**
     * @title Multimedia page configuration
     * @description Labels for the Multimedia page
     * @title_de Konfiguration der Multimedia-Seite
     * @description_de Beschriftungen für die Multimedia-Seite
     */
    multimediaPage: MultimediaPageConfiguration
    /**
     * @title Exhibitor News page configuration
     * @description Labels for the Exhibitor News page
     * @title_de Konfiguration der Exhibitor-News-Seite
     * @description_de Labels für die Exhibitor-News-Seite
     */
    newsPage: ExhibitorNewsPageConfiguration
    /**
     * @title Organizer News page configuration
     * @description Labels for the Organizer News page
     * @title_de Konfiguration der Organizer-News-Seite
     * @description_de Labels für die Organizer-News-Seite
     */
    organizerNewsPage: OrganizerNewsPageConfiguration
    /**
     * @title Stories page configuration
     * @description Labels for the Stories page
     * @title_de Konfiguration der Stories-Seite
     * @description_de Labels für die Stories-Seite
     */
    storyNewsPage: StoryNewsPageConfiguration
    /**
     * @title Event dates page configuration
     * @description Labels for the Event dates page
     * @title_de Konfiguration der Veranstaltungsterminseite
     * @description_de Konfiguration für die Elemente der Veranstaltungsterminseite
     */
    eventDatesPage: EventDatesPageConfiguration
    /**
     * @title Exhibitor Events page configuration
     * @description Labels for the Exhibitor Events page
     * @title_de Konfiguration der Seite Aussteller-Events
     * @description_de Konfiguration für die Elemente der Seite Aussteller-Events
     */
    nonCuratedEventDatesPage: NonCuratedEventDatesPageConfiguration
    /**
     * @title Showrooms page configuration
     * @description Labels for the Showrooms page
     * @title_de --
     * @description_de --
     */
    showroomsPage: ShowroomsPageConfiguration
    /**
     * @title Coupons page configuration
     * @description Labels for the Coupons page
     * @title_de --
     * @description_de --
     */
    couponsPage: CouponsPageConfiguration
    /**
     * @title Jobs page configuration
     * @description Labels for the Jobs page
     * @title_de --
     * @description_de --
     */
    jobsPage: JobsPageConfiguration
    /**
     * @title Notification templates page configuration
     * @description Labels for the Notification templates page
     * @title_de Seitenkonfiguration „Notification-Templates“
     * @description_de Beschriftungen für die Seite „Notification-Templates“
     */
    notificationTemplatesPage: NotificationTemplatesPageConfiguration
    /**
     * @title Print page configuration
     * @description Labels for the Print page
     * @title_de --
     * @description_de --
     */
    printPage: PrintPageConfiguration
    /**
     * @title Validation messages page configuration
     * @description Configuration for the validation messages
     * @title_de --
     * @description_de --
     */
    validationMessages: ValidationMessagesConfiguration
    /**
     * @title Error messages page configuration
     * @description Configuration for the validation messages
     * @title_de --
     * @description_de --
     */
    errorMessages: ErrorMessagesConfiguration
    /**
     * @title Categories countable
     * @description Categories allow to be counted differently
     * @title_de Kategorien zählbar
     * @description_de Kategorien können unterschiedlich gezählt werden
     */
    countableCategories: CategoriesCountableConfiguration
}

export interface CategoriesCountableConfiguration {
    /**
     * @title Categories countable
     * @description Categories allow to be counted differently
     * @title_de Kategorien zählbar
     * @description_de Kategorien können unterschiedlich gezählt werden
     */
    categoriesCountableCategories: string[]
    /**
     * @title Categories countable in Job page
     * @description Categories allow to be counted in Job page
     * @title_de Kategorien zählbar in Job-Seite
     * @description_de Kategorien können auf der Job-Seite gezählt werden
     */
    jobCountableCategories: string[]
    /**
     * @title Categories countable in News page
     * @description Categories allow to be counted in News page
     * @title_de Kategorien zählbar in News-Seite
     * @description_de Kategorien können auf der News-Seite gezählt werden
     */
    newsCountableCategories: string[]
    /**
     * @title Categories countable in Product page
     * @description Categories allow to be counted in Product page
     * @title_de Kategorien zählbar in Product-Seite
     * @description_de Kategorien können auf der Product-Seite gezählt werden
     */
    productCountableCategories: string[]
    /**
     * @title Categories countable in Trademark page
     * @description Categories allow to be counted in Trademark page
     * @title_de Kategorien zählbar in Trademark-Seite
     * @description_de Kategorien können auf der Trademark-Seite gezählt werden
     */
    trademarkCountableCategories: string[]
    /**
     * @title Limit exceeded warning message
     * @description Message to be displayed when the category selection limit has been exceeded
     * @title_de --
     * @description_de --
     */
    warningMessage: LocalizedString
}

export interface ValidationMessagesConfiguration {
    /**
     * @title Company name missing error
     * @description Error message for missing company name
     * @title_de --
     * @description_de --
     */
    missingCompanyName: LocalizedString
    /**
     * @title Company long description missing error
     * @description Error message for missing long company description
     * @title_de Unternehmens Langbeschreibung-Fehler
     * @description_de Fehlermeldung für fehlende ausführliche Unternehmensbeschreibung
     */
    missingCompanyLongDescription: LocalizedString
    /**
     * @title Company short description missing error
     * @description Error message for missing short company description
     * @title_de Unternehmens Kurzbeschreibung Fehler
     * @description_de Fehlermeldung für fehlende kurze Unternehmensbeschreibung
     */
    missingCompanyShortDescription: LocalizedString
    /**
     * @title Company address missing error
     * @description Error message for missing company address
     * @title_de --
     * @description_de --
     */
    missingCompanyAddress: LocalizedString
    /**
     * @title Company postcode missing error
     * @description Error message for missing company postcode
     * @title_de --
     * @description_de --
     */
    missingCompanyPostcode: LocalizedString
    /**
     * @title Company city missing error
     * @description Error message for missing company city
     * @title_de --
     * @description_de --
     */
    missingCompanyCity: LocalizedString
    /**
     * @title Company name missing error
     * @description Error message for missing company name
     * @title_de --
     * @description_de --
     */
    missingCompanyCountry: LocalizedString
    /**
     * @title Email invalid error
     * @description Error message for invalid email name
     * @title_de --
     * @description_de --
     */
    invalidCompanyEmail: LocalizedString
    /**
     * @title Email invalid error
     * @description Error message for invalid email name
     * @title_de --
     * @description_de --
     */
    invalidCompanyEmail2: LocalizedString
    /**
     * @title Phone invalid error
     * @description Error message for invalid phone number
     * @title_de --
     * @description_de --
     */
    missingCompanyPhone: LocalizedString
    /**
     * @title Mobile invalid error
     * @description Error message for invalid mobile number
     * @title_de --
     * @description_de --
     */
    missingCompanyMobile: LocalizedString
    /**
     * @title Fax invalid error
     * @description Error message for invalid fax name
     * @title_de --
     * @description_de --
     */
    missingCompanyFax: LocalizedString
    /**
     * @title Email invalid error
     * @description Error message for invalid email name
     * @title_de --
     * @description_de --
     */
    missingCompanyEmail: LocalizedString
    /**
     * @title Website invalid error
     * @description Error message for invalid website name
     * @title_de --
     * @description_de --
     */
    missingCompanyWebsite: LocalizedString
    /**
     * @title Size invalid error
     * @description Error message for invalid size
     * @title_de --
     * @description_de --
     */
    missingCompanySize: LocalizedString
    /**
     * @title Industry invalid error
     * @description Error message for invalid industry
     * @title_de --
     * @description_de --
     */
    missingCompanyIndustry: LocalizedString
    /**
     * @title FoundingYear invalid error
     * @description Error message for invalid FoundingYear
     * @title_de --
     * @description_de --
     */
    missingCompanyFoundingYear: LocalizedString
    /**
     * @title Link name missing error
     * @description Error message for missing link name
     * @title_de --
     * @description_de --
     */
    missingLinkName: LocalizedString
    /**
     * @title Link url missing error
     * @description Error message for missing link url
     * @title_de --
     * @description_de --
     */
    missingLinkUrl: LocalizedString
    /**
     * @title Product name missing error
     * @description Error message for missing product name
     * @title_de --
     * @description_de --
     */
    missingProductName: LocalizedString
    /**
     * @title Product collection missing error
     * @description Error message for missing product collection
     * @title_de --
     * @description_de --
     */
    missingProductCollection: LocalizedString
    /**
     * @title Product start date is greater than or equal to current date error
     * @description Error message for start date greater or equal than current date
     * @title_de --
     * @description_de --
     */
    dateGreaterOrEqualThanCurrentDate: LocalizedString
    /**
     * @title Product end date error greater than current date error
     * @description Error message for end date grater than current date
     * @title_de --
     * @description_de --
     */
    dateGreaterThanCurrentDate: LocalizedString
    /**
     * @title Product publication end date missing error
     * @description Error message for missing product publication end date
     * @title_de --
     * @description_de --
     */
    invalidProductPublicationEndDate: LocalizedString
    /**
     * @title Trademark name missing error
     * @description Error message for missing trademark name
     * @title_de --
     * @description_de --
     */
    missingTrademarkName: LocalizedString
    /**
     * @title first name missing error
     * @description Error message for missing first name
     * @title_de --
     * @description_de --
     */
    missingFirstName: LocalizedString
    /**
     * @title Last name name missing error
     * @description Error message for missing last name
     * @title_de --
     * @description_de --
     */
    missingLastName: LocalizedString
    /**
     * @title Email missing error
     * @description Error message for missing email
     * @title_de --
     * @description_de --
     */
    missingEmail: LocalizedString
    /**
     * @title Email invalid error
     * @description Error message for invalid email name
     * @title_de --
     * @description_de --
     */
    invalidEmail: LocalizedString
    /**
     * @title Role missing error
     * @description Error message for missing role
     * @title_de --
     * @description_de --
     */
    missingRole: LocalizedString
    /**
     * @title Media title missing error
     * @description Error message for missing media title
     * @title_de --
     * @description_de --
     */
    missingMediaTitle: LocalizedString
    /**
     * @title News title missing error
     * @description Error message for missing News title
     * @title_de --
     * @description_de --
     */
    missingNewsTitle: LocalizedString
    /**
     * @title News date missing error
     * @description Error message for missing News date
     * @title_de --
     * @description_de --
     */
    missingNewsDate: LocalizedString
    /**
     * @title Story news media missing error
     * @description Error message for missing Story medias
     * @title_de --
     * @description_de --
     */
    missingNewsStoryMedias: LocalizedString
    /**
     * @title Event date name missing error
     * @description Error message for missing event date name
     * @title_de --
     * @description_de --
     */
    missingEventDateName: LocalizedString
    /**
     * @title Event date location missing error
     * @description Error message for missing event date location
     * @title_de --
     * @description_de --
     */
    missingEventDateLocation: LocalizedString
    /**
     * @title Event date start time invalid
     * @description Error message for invalid event date start time
     * @title_de --
     * @description_de --
     */
    invalidEventDateStartTime: LocalizedString
    /**
     * @title Event date end time invalid
     * @description Error message for invalid event date end time
     * @title_de --
     * @description_de --
     */
    invalidEventDateEndTime: LocalizedString
    /**
     * @title Showroom title missing error
     * @description Error message for missing showroom title
     * @title_de --
     * @description_de --
     */
    missingShowroomTitle: LocalizedString
    /**
     * @title Coupon title missing error
     * @description Error message for missing coupon title
     * @title_de --
     * @description_de --
     */
    missingCouponName: LocalizedString
    /**
     * @title Coupon Url missing error
     * @description Error message for missing coupon url
     * @title_de --
     * @description_de --
     */
    missingCouponValidUrl: LocalizedString
    /**
     * @title Job title missing error
     * @description Error message for missing job title
     * @title_de --
     * @description_de --
     */
    missingJobTitle: LocalizedString
    /**
     * @title Job start date missing error
     * @description Error message for missing start date
     * @title_de --
     * @description_de --
     */
    missingJobValidStartDate: LocalizedString
    /**
     * @title Job start displaying date missing error
     * @description Error message for missing start displaying date
     * @title_de --
     * @description_de --
     */
    missingJobValidStartDisplaying: LocalizedString
    /**
     * @title Company name missing error
     * @description Error message for missing company name
     * @title_de --
     * @description_de --
     */
    missingPrintCompanyName: LocalizedString
    /**
     * @title Address missing error
     * @description Error message for missing address
     * @title_de --
     * @description_de --
     */
    missingPrintAddress: LocalizedString
    /**
     * @title Zip code missing error
     * @description Error message for missing zip code
     * @title_de --
     * @description_de --
     */
    missingPrintZipcode: LocalizedString
    /**
     * @title City missing error
     * @description Error message for missing city
     * @title_de --
     * @description_de --
     */
    missingPrintCity: LocalizedString
    /**
     * @title Country missing error
     * @description Error message for missing country
     * @title_de --
     * @description_de --
     */
    missingPrintCountry: LocalizedString
    /**
     * @title Destination missing error
     * @description Error message for missing destination
     * @title_de --
     * @description_de --
     */
    missingDestinations: LocalizedString
    /**
     * @title Date & time missing error
     * @description Error message for missing destination
     * @title_de --
     * @description_de --
     */
    missingDateTime: LocalizedString
    /**
     * @title Coupon's valid start date missing error
     * @description Error message for missing Coupon's valid start date
     * @title_de --
     * @description_de --
     */
    missingValidStartDate: LocalizedString
    /**
     * @title Coupon's valid end date missing error
     * @description Error message for missing Coupon's valid end date
     * @title_de --
     * @description_de --
     */
    missingValidEndDate: LocalizedString
    /**
     * @title Success message title
     * @description Title of success message popup
     * @title_de --
     * @description_de --
     */
    successMessageTitle: LocalizedString
    /**
     * @title Success message text
     * @description Text of success message popup
     * @title_de --
     * @description_de --
     */
    successMessageText: LocalizedString
    /**
     * @title Warning message title
     * @description Title of warning message popup
     * @title_de --
     * @description_de --
     */
    warningMessageTitle: LocalizedString
    /**
     * @title Error message title
     * @description Title of error message popup
     * @title_de --
     * @description_de --
     */
    errorMessageTitle: LocalizedString
}

export interface ErrorMessagesConfiguration {
    /**
     * @title Invalid file type error
     * @description Error message for invalid file type
     * @title_de --
     * @description_de --
     */
    invalidFileType: LocalizedString
}
export interface ModalPublishConfiguration {
    /**
     * @title Modal's title
     * @description Title for the publish modal
     * @title_de Modal's title
     * @description_de Title for the publish modal
     */
    title: LocalizedString
    /**
     * @title Publish content text
     * @description Content text for the publish modal
     * @title_de Publish content text
     * @description_de Content text for the publish modal
     */
    label: LocalizedString
    /**
     * @title Failed validation field names mappings
     * @description Mapping between failed validation field names and the labels displayed on the publish modal
     * @title_de --
     * @description_de --
     */
    fieldNamesConfig: ValidationFieldNamesConfiguration

    /**
     * @title Error message visibility duration
     * @description Duration of visibility of error message in millisecionds
     * @title_de --
     * @description_de --
     */
    visibilityDuration: number
}

export interface LanguageSwitchConfiguration {
    /**
     * @title English option label
     * @description Label for the English option in the language switch
     * @title_de Englisches Optionsetikett
     * @description_de Beschriftung für die englische Option in der Sprachumschaltung
     */
    lblEnOption: LocalizedString
    /**
     * @title German option label
     * @description Label for the German option in the language switch
     * @title_de Deutsches Optionsetikett
     * @description_de Beschriftung für die deutsche Option in der Sprachumschaltung
     */
    lblDeOption: LocalizedString
    /**
     * @title German option label
     * @description Label for the German option in the language switch
     * @title_de Deutsches Optionsetikett
     * @description_de Beschriftung für die deutsche Option in der Sprachumschaltung
     */
    lblStandardTemplate: LocalizedString
    /**
     * @title German option label
     * @description Label for the German option in the language switch
     * @title_de Deutsches Optionsetikett
     * @description_de Beschriftung für die deutsche Option in der Sprachumschaltung
     */
    lblTranslationTemplate: LocalizedString
}

export interface ActionbarConfiguration {
    /**
     * @title Publish button
     * @description Label for the Publish button
     * @title_de Publish button
     * @description_de Label for the Publish button
     */
    btnPublish: LocalizedString
    /**
     * @title Discard button
     * @description Label for the Discard button
     * @title_de Discard button
     * @description_de Label for the Discard button
     */
    btnDiscard: LocalizedString
    /**
     * @title Discard modal title
     * @description Title of the discard modal
     * @title_de Discard modal title
     * @description_de Title of the discard modal
     */
    modalDiscardTitle: LocalizedString
    /**
     * @title Discard modal text
     * @description Text the discard modal
     * @title_de Discard modal text
     * @description_de Text the discard modal
     */
    modalDiscardText: LocalizedString
    /**
     * @title Discard modal OK button text
     * @description Label for the OK button in discard modal
     * @title_de Discard modal OK button text
     * @description_de Label for the OK button in discard modal
     */
    modalDiscardOK: LocalizedString
    /**
     * @title Discard modal cancel button text
     * @description Label for the cancel button in discard modal
     * @title_de Discard modal cancel button text
     * @description_de Label for the cancel button in discard modal
     */
    modalDiscardCancel: LocalizedString
}

export enum SidebarSection {
    PROFILE = "PROFILE",
    CATEGORY = "CATEGORY",
    LICENCES = "LICENCES",
    LINKS = "LINKS",
    PRODUCTS = "PRODUCTS",
    PRODUCTS_FOR_COLLECTIONS = "PRODUCTS_FOR_COLLECTIONS",
    TRADEMARKS = "TRADEMARKS",
    TEAM = "TEAM",
    MULTIMEDIA = "MULTIMEDIA",
    EXHIBITOR_NEWS = "EXHIBITOR_NEWS",
    ORGANIZER_NEWS = "ORGANIZER_NEWS",
    STORY_NEWS = "STORY_NEWS",
    EVENTDATES = "EVENTDATES",
    EXHIBITOR_EVENTS = "EXHIBITOR_EVENTS",
    SHOWROOMS = "SHOWROOMS",
    COUPONS = "COUPONS",
    JOBS = "JOBS",
    NOTIFICATION_TEMPLATES = "NOTIFICATION_TEMPLATES",
    PRINT = "PRINT",
    CUSTOM_LINK = "CUSTOM_LINK"
}

export interface SidebarConfiguration {
    /**
     * @title Items order
     * @description Order of items in the sidebar navigation, item is not shown in the profile if not listed here
     * @title_de Ordnung der Elemente
     * @description_de Ordnung der Elemente in der Sidebar-Navigation, Element wird nicht im Profil angezeigt, wenn es hier nicht aufgeführt ist
     */
    sectionsOrder: SidebarSection[]
    /**
     * @title Profile label
     * @description Label for the Profile menu entry
     * @title_de Profil label
     * @description_de Bezeichnung für den Menüeintrag Profil
     */
    lblProfile: LocalizedString
    /**
     * @title Categories label
     * @description Label for the Categories menu entry
     * @title_de Categories label
     * @description_de  Beschriftung für den Menüeintrag Kategorien
     */
    lblCategories: LocalizedString
    /**
     * @title Licences label
     * @description Label for the Licences menu entry
     * @title_de Lizenzen label
     * @description_de Beschriftung für den Menüeintrag "Lizenzen"
     */
    lblLicences: LocalizedString
    /**
     * @title Licences URL
     * @description URL for the Licences menu entry
     * @title_de Lizenzen URL
     * @description_de URL für den Menüeintrag "Lizenzen"
     */
    urlLicences: string
    /**
     * @title Links label
     * @description Label for the Links menu entry
     * @title_de Links label
     * @description_de Beschriftung für den Menüeintrag "Links"
     */
    lblLinks: LocalizedString
    /**
     * @title Products label
     * @description Label for the Products menu entry
     * @title_de Produktetikett
     * @description_de  Bezeichnung für den Menüeintrag Produkte
     */
    lblProducts: LocalizedString
    /**
     * @title Products for Collections label
     * @description Label for the Products for Collections menu entry
     * @title_de Produktetikett
     * @description_de  Bezeichnung für den Menüeintrag Produkte
     */
    lblProductsForCollections: LocalizedString
    /**
     * @title Trademarks label
     * @description Label for the Trademarks menu entry
     * @title_de Handelsmarken Label
     * @description_de Bezeichnung für den Menüeintrag Marken
     */
    lblTrademarks: LocalizedString
    /**
     * @title Team label
     * @description Label for the Team menu entry
     * @title_de Mannschaft label
     * @description_de Bezeichnung für den Menüeintrag Team
     */
    lblTeam: LocalizedString
    /**
     * @title Multimedia label
     * @description Label for the Multimedia menu entry
     * @title_de Multimedia label
     * @description_de Beschriftung für den Menüeintrag Multimedia
     */
    lblMultimedia: LocalizedString

    /**
     * @title Exhibitor News label
     * @description Label for the Exhibitor News menu entry
     * @title_de Exhibitor-News-Label
     * @description_de  Bezeichnung für den Menüeintrag Exhibitor-News
     */
    lblExhibitorNews: LocalizedString

    /**
     * @title Organizer News label
     * @description Label for the Organizer News menu entry
     * @title_de Organizer-News-Label
     * @description_de  Bezeichnung für den Menüeintrag Organizer-News
     */
    lblOrganizerNews: LocalizedString

    /**
     * @title Stories label
     * @description Label for the Stories menu entry
     * @title_de Stories-Label
     * @description_de  Bezeichnung für den Menüeintrag Stories
     */
    lblStoryNews: LocalizedString

    /**
     * @title Event dates label
     * @description Label for the Event dates menu entry
     * @title_de Label für Veranstaltungsdaten
     * @description_de Bezeichnung für den Menüeintrag Veranstaltungsdaten
     */
    lblEventDate: LocalizedString

    /**
     * @title Exhibitor Events label
     * @description Label for the Exhibitor Events menu entry
     * @title_de Label für Ausstellerevents
     * @description_de Bezeichnung für den Menüeintrag Ausstellerevents
     */
    lblExhibitorEvents: LocalizedString

    /**
     * @title Showrooms label
     * @description Label for the Showrooms menu entry
     * @title_de Label für Ausstellungsräume
     * @description_de Bezeichnung für den Menüeintrag Ausstellungsräume
     */
    lblShowrooms: LocalizedString
    /**
     * @title Coupons label
     * @description Label for the Coupons menu entry
     * @title_de Label für Gutscheine
     * @description_de Bezeichnung für den Menüeintrag Gutscheine
     */
    lblCoupons: LocalizedString
    /**
     * @title Jobs label
     * @description Label for the Jobs menu entry
     * @title_de Label für Stellenanzeigen
     * @description_de Bezeichnung für den Menüeintrag Stellenanzeigen
     */
    lblJobs: LocalizedString
    /**
     * @title Print label
     * @description Label for the Print menu entry
     * @title_de Label für Print
     * @description_de Bezeichnung für den Menüeintrag Print
     */
    lblPrint: LocalizedString
    /**
     * @title Lead + Meet label
     * @description Label for the Lead + Meet menu entry
     * @title_de Label für Lead + Meet
     * @description_de Bezeichnung für den Menüeintrag Lead + Meet
     */
    lblNotificationTemplates: LocalizedString
    /**
     * @title Custom link label
     * @description Label for the custom link menu entry
     * @title_de Benutzerdefiniertes Link-Label
     * @description_de Label für den benutzerdefinierten Linkmenüeintrag
     */
    lblCustomLink: LocalizedString
    /**
     * @title Custom link URL
     * @description URL for the custom link menu entry
     * @title_de Benutzerdefiniertes Link-URL
     * @description_de URL für den benutzerdefinierten Linkmenüeintrag
     */
    urlCustomLink: LocalizedString
    /**
     * @title Event Shop button label
     * @description Label for the Event Shop button
     * @title_de Label für Event Shop button
     * @description_de Bezeichnung für den Event-Shop-Button
     */
    lblEventShop: LocalizedString
    /**
     * @title Event Shop button URL
     * @description URL for the Event Shop button
     * @title_de URL für Event Shop button
     * @description_de Bezeichnung für den Event-Shop-Button
     */
    urlEventShop: LocalizedString
    /**
     * @title Show Event Shop button
     * @description Toggle visibility of Event Shop button
     * @title_de Event-Shop-Button anzeigen
     * @description_de Schalten Sie die Sichtbarkeit des Event-Shop-Buttons um
     */
    showEventShop: boolean
    /**
     * @title Event Guide button label
     * @description Label for the Event Guide button
     * @title_de Label für Event Guide button
     * @description_de Bezeichnung für den Event-Guide-Button
     */
    lblEventGuide: LocalizedString
    /**
     * @title Event Guide button URL
     * @description URL for the Event Guide button
     * @title_de URL für Event Guide button
     * @description_de Bezeichnung für den Event-Guide-Button
     */
    urlEventGuide: string
    /**
     * @title Show Event Guide button
     * @description Toggle visibility of Event Guide button
     * @title_de Event-Guide-Button anzeigen
     * @description_de Schalten Sie die Sichtbarkeit des Event-Guide-Buttons um
     */
    showEventGuide: boolean
}

export enum ProfileSection {
    LOGO = "LOGO",
    VIDEO = "VIDEO",
    BASIC_INFO = "BASIC_INFO",
    COMPANY_SORT = "COMPANY_SORT",
    DESCRIPTION = "DESCRIPTION",
    KEYWORDS = "KEYWORDS",
    SOCIAL_MEDIA = "SOCIAL_MEDIA",
    IFRAME = "IFRAME",
    CONSENT_CONTENT = "CONSENT_CONTENT"
}

export interface BuyMoreConfiguration {
    /**
     * @title Page
     * @description Page for which the Button will be shown
     * @title_de Seite
     * @description_de Seite, für die der Button angezeigt wird
     */
    page: SidebarSection
    /**
     * @title Link in Event Shop
     * @description Link for the particular service key product in the Event Shop
     * @title_de Link im Event-Shop
     * @description_de Link zum jeweiligen Service-Key-Produkt im Event-Shop
     */
    link: LocalizedString
}

export interface TopBarConfiguration {
    /**
     * @title "Buy more" configurations
     * @description Configurations for "Buy more" button for separate pages
     * @title_de „Mehr kaufen“-Konfigurationen
     * @description_de Konfigurationen für den „Mehr kaufen“-Button für einzelne Seiten
     */
    buyMoreConfigs: BuyMoreConfiguration[]
    /**
     * @title "Buy more" button text
     * @description Text for the "Buy more" button
     * @title_de „Mehr kaufen“-Button-Text
     * @description_de Text für den „Mehr kaufen“-Button
     */
    buyMoreButtonText: LocalizedString
    /**
     * @title QR code download button text
     * @description Text for the QR code download button
     * @title_de Text der QR-Code-Download-Button
     * @description_de Text für den QR-Code-Download-Button
     */
    downloadQrCodeButtonText: LocalizedString
}

export enum EventCloudProduct {
    EVENT_GUIDE = "EVENT_GUIDE",
    MOBILE_GUIDE = "MOBILE_GUIDE",
    WEB_WIDGETS = "WEB_WIDGETS"
}

export interface EventCloudProductsConfiguration {
    /**
     * @title Event Cloud products list
     * @description List of Event Cloud products that are in use for this topic
     * @title_de Liste der Event-Cloud-Produkte
     * @description_de Liste der Event-Cloud-Produkte, die für dieses Topic verwendet werden
     */
    list: EventCloudProduct[]
    /**
     * @title Name of Event Guide
     * @description Customized name of the product Event Guide
     * @title_de Name des Event-Guides
     * @description_de Customized name of the product Event Guide
     */
    nameEventGuide: string
    /**
     * @title Name of Mobile Guide
     * @description Customized name of the product Mobile Guide
     * @title_de Name des Mobile-Guides
     * @description_de Customized name of the product Mobile Guide
     */
    nameMobileGuide: string
    /**
     * @title Name of Web Widgets
     * @description Customized name of the product Web Widgets
     * @title_de Name des Web Widgets
     * @description_de Customized name of the product Web Widgets
     */
    nameWebWidgets: string
    /**
     * @title Switcher title
     * @description Title of the options switcher of the Event Cloud products
     * @title_de Switcher-Titel
     * @description_de Titel des Switchers der Event-Cloud-Produkte
     */
    titleSwitcher: LocalizedString
}

export interface QrCodesConfiguration {
    /**
     * @title Enabled
     * @description Toggle usage of QR code download functionality
     * @title_de Ermöglicht
     * @description_de Schalten Sie die Nutzung der QR-Code-Download-Funktion um
     */
    enabled: boolean
    /**
     * @title Format
     * @description Format of the image (SVG/PNG)
     * @title_de Format
     * @description_de Format des Bildes (SVG/PNG)
     */
    format: QrCodeFormat
    /**
     * @title Modal title
     * @description Text for the title of the download modal
     * @title_de Modals Titel
     * @description_de Text für den Titel des Download-Modals
     */
    modalTitle: LocalizedString
    /**
     * @title Download button text
     * @description Text for the download button in the download modal
     * @title_de Text des Download-Buttons
     * @description_de Text für den Download-Button im Download-Modal
     */
    downloadButtonText: LocalizedString
    /**
     * @title Cancel button text
     * @description Text of the cancellation button in the download modal
     * @title_de Text des Abbruchbuttons
     * @description_de Text des Abbruchbuttons im Download-Modal
     */
    cancelButtonText: LocalizedString
    /**
     * @title Error title
     * @description Text for the title of the error version of the download modal
     * @title_de Fehlertitel
     * @description_de Text für den Titel der Fehlerversion des Download-Modals
     */
    errorTitle: LocalizedString
    /**
     * @title Error message
     * @description Text for the "Buy more" button
     * @title_de Fehlermeldung
     * @description_de Konfigurationen für den „Mehr kaufen“-Button für einzelne Seiten
     */
    errorMessage: LocalizedString
}

export interface ProfilePageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Sections order
     * @description Order of sections in the profile page, section is not shown in the profile if not listed here
     * @title_de Sektionsordnung
     * @description_de Ordnung der Sektionen auf der Profilseite, Sektion wird nicht im Profil angezeigt, wenn sie hier nicht aufgeführt ist
     */
    sectionsOrder: ProfileSection[]
    /**
     * @title Upload Section config
     * @description Label for the Upload profile page entry
     * @title_de Hochladen label
     * @description_de Bezeichnung für den Eintrag der Profilseite hochladen
     */
    sctUpload: UploadSectionConfiguration
    /**
     * @title Video Section config
     * @description Label for the Video profile page entry
     * @title_de Konfiguration des Videoabschnitts
     * @description_de Beschriftung für den Eintrag auf der Videoprofilseite
     */
    sctVideo: VideoSectionConfiguration
    /**
     * @title Basic Info Section config
     * @description Label for the Basic Info Section profile page entry
     * @title_de Konfiguration des Abschnitts "Basic Info"
     * @description_de Bezeichnung für den Profilseiteneintrag des Abschnitts "Grundlegende Informationen"
     */
    sctBasicInfo: BasicInfoSectionConfiguration
    /**
     * @title Basic Info Section readonly config
     * @description Label for the Basic Info Section readonly profile page entry
     * @title_de Konfiguration des schreibgeschützten Bereichs "Basisinformationen
     * @description_de Etikett für den Eintrag auf der Profilseite des Abschnitts Basisinfos, der nur gelesen werden kann"
     */
    sctBasicInfoReadonly: BasicInfoSectionConfigurationReadonly
    /**
     * @title Basic Info Section mandatory config
     * @description Label for the Basic Info Section mandatory profile page entry
     * @title_de Grundlegende Informationen Abschnitt obligatorische Konfiguration
     * @description_de Etikett für den obligatorischen Eintrag auf der Profilseite im Abschnitt Basisinformationen
     */
    sctBasicInfoMandatory: BasicInfoSectionConfigurationMandatory
    /**
     * @title Description Section config
     * @description Label for the Description Section page profile entry
     * @title_de Beschreibung Abschnitt konfiguration
     * @description_de Beschriftung für den Profileintrag der Seite "Beschreibungsabschnitt"
     */
    sctDescription: DescriptionSectionConfiguration
    /**
     * @title Description Section mandatory config
     * @description Label for the Description Section mandatory profile page entry
     * @title_de Beschreibung Abschnitt obligatorische Konfiguration
     * @description_de Beschriftung für den obligatorischen Eintrag auf der Profilseite im Abschnitt "Beschreibungsabschnitt"
     */
    sctDescriptionMandatory: DescriptionSectionConfigurationMandatory
    /**
     * @title Keywords Section config
     * @description Label for the Keywords Section page profile entry
     * @title_de Konfiguration des Abschnitts "Schlüsselwörter"
     * @description_de Beschriftung für den Profileintrag der Seite "Keywords Section"
     */
    sctKeywords: KeywordsSectionConfiguration
    /**
     * @title Social Media Section config
     * @description Label for the Social Media Section page profile entry
     * @title_de Social Media Section Konfiguration
     * @description_de Label für den Profileintrag der Social Media Section-Seite
     */
    sctSocialMedia: SocialMediaSectionConfiguration
    /**
     * @title Frame Section config
     * @description Label for the Frame Section page profile entry
     * @title_de Konfiguration des Rahmenabschnitts
     * @description_de Beschriftung für den Profileintrag der Seite "Frame Section"
     */
    sctiFrame: iFrameSectionConfiguration
    /**
     * @title Consent Content Section config
     * @description Label for the Consent Content Section page profile entry
     * @title_de Konfiguration des Zustimmungsinhaltsbereichs
     * @description_de Bezeichnung für den Profileintrag der Seite "Zustimmungsinhalt"
     */
    sctConsentContent: ConsentContentSectionConfiguration
}

export interface UploadSectionConfiguration {
    /**
     * @title Upload label
     * @description Label for the Upload input
     * @title_de Label hochladen
     * @description_de Label für den Upload-Eingang
     */
    lblUpload: LocalizedString
    /**
     * @title Company Logo placeholder
     * @description Label for the Company Logo input
     * @title_de Platzhalter für das Firmenlogo
     * @description_de Etikett für die Eingabe des Firmenlogos
     */
    lblCompanyLogo: LocalizedString
    /**
     * @title Company Logo Title label
     * @description Label for the Company Logo Title input
     * @title_de Titelbezeichnung des Firmenlogos
     * @description_de Beschriftung für die Eingabe des Firmenlogos
     */
    hntTitleCompanyLogo: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntPropCompanyLogo: LocalizedString
    /**
     * @title Logo Browse files Button
     * @description Label for the Button Browse files
     * @title_de Logo Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFilesLogo: LocalizedString
    /**
     * @title Logo Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Logo Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImgLogo: LocalizedString
    /**
     * @title "Buy logo" button text
     * @description Text for the "Buy logo" button
     * @title_de „Logo kaufen“-Button-Text
     * @description_de Text für den „Logo kaufen“-Button
     */
    btnBuyLogoLabel: LocalizedString
    /**
     * @title "Buy logo" shop link
     * @description Link for the "logo" service key product in the Event Shop
     * @title_de Shop-Link „Logo kaufen“.
     * @description_de Link zum Service-Key-Produkt „logo“ im Event-Shop
     */
    btnBuyLogoLink: LocalizedString
    /**
     * @title Company Background placeholder
     * @description Label for the Company Background input
     * @title_de Platzhalter "Unternehmenshintergrund"
     * @description_de Beschriftung für die Eingabe "Unternehmenshintergrund"
     */
    lblCompanyBackgroundImage: LocalizedString
    /**
     * @title Company Background Title label (Desktop)
     * @description Label for the Company Background Title input
     * @title_de Titelbezeichnung des Firmenhintergrundes (Desktop)
     * @description_de Beschriftung für die Eingabe des Unternehmenshintergrundtitels
     */
    hntTitleDesktopBackgroundImage: LocalizedString
    /**
     * @title Company Background Properties placeholder (Desktop)
     * @description Label for the Company Background Properties input
     * @title_de Eigenschaften des Unternehmenshintergrund (Desktop)
     * @description_de Bezeichnung für die Eingabe "Unternehmenshintergrundeigenschaften"
     */
    hntPropDesktopBackgroundImage: LocalizedString
    /**
     * @title Company Background Title label (Mobile)
     * @description Label for the Company Background Title input
     * @title_de Titelbezeichnung des Firmenhintergrundes (Mobile)
     * @description_de Beschriftung für die Eingabe des Unternehmenshintergrundtitels
     */
    hntTitleMobileBackgroundImage: LocalizedString
    /**
     * @title Company Background Properties placeholder (Mobile)
     * @description Label for the Company Background Properties input
     * @title_de Eigenschaften des Unternehmenshintergrund (Mobile)
     * @description_de Bezeichnung für die Eingabe "Unternehmenshintergrundeigenschaften"
     */
    hntPropMobileBackgroundImage: LocalizedString
    /**
     * @title Company Background Browse files Button
     * @description Label for the Button Browse files
     * @title_de Unternehmenshintergrund Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFilesBackgroundImage: LocalizedString
    /**
     * @title Company Background Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Unternehmenshintergrund Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImgBackgroundImage: LocalizedString
    /**
     * @title "Buy key visual" button text
     * @description Text for the "Buy key visual" button
     * @title_de „Keyvisual kaufen“-Button-Text
     * @description_de Text für den „Keyvisual kaufen“-Button
     */
    btnBuyHeaderGraphicLabel: LocalizedString
    /**
     * @title "Buy key visual" shop link
     * @description Link for the "headerGraphic" service key product in the Event Shop
     * @title_de Shop-Link „Keyvisual kaufen“.
     * @description_de Link zum Service-Key-Produkt „headerGraphic“ im Event-Shop
     */
    btnBuyHeaderGraphicLink: LocalizedString
}

export interface VideoSectionConfiguration {
    /**
     * @title Company video label
     * @description Label for the Company video input
     * @title_de Videolabel des Unternehmens
     * @description_de Label für den Firmenvideoeingang
     */
    lblCompanyVideo: LocalizedString
    /**
     * @title Company thumbnail label
     * @description Label for the Company thumbnail input
     * @title_de Beschriftung des Miniaturansichtsabschnitts
     * @description_de Beschriftung für die Eingabe des Miniaturansichtsabschnitts
     */
    lblThumbnail: LocalizedString
    /**
     * @title Company video placeholder
     * @description Label for the Company video input
     * @title_de Platzhalter "Unternehmenshintergrund"
     * @description_de Beschriftung für die Eingabe "Unternehmenshintergrund"
     */
    lblVideo: LocalizedString
    /**
     * @title Company thumbnail title label
     * @description Label for the Company thumbnail title input
     * @title_de Titelbezeichnung des Firmenbilds
     * @description_de Beschriftung für die Eingabe des Unternehmensbildtitels
     */
    hntTitleThumbnail: LocalizedString
    /**
     * @title Company thumbnail properties placeholder
     * @description Label for the Company thumbnail properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntPropThumbnail: LocalizedString
    /**
     * @title Company video title label
     * @description Label for the Company video title input
     * @title_de TLabel für Firmenvideotitel
     * @description_de Beschriftung für die Eingabe des Unternehmensvideotitels
     */
    hntTitleVideo: LocalizedString
    /**
     * @title Company video properties placeholder
     * @description Placeholder for the Company video properties input
     * @title_de Platzhalter für Unternehmensvideoeigenschaften
     * @description_de Platzhalter für die Eingabe "Unternehmensvideoeigenschaften"
     */
    hntPropVideo: LocalizedString
    /**
     * @title  Browse files Buttonhnt
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Delete current video Button
     * @description Label for the Button Delete current video
     * @title_de Aktuelles Bild video Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild video
     */
    btnDeleteVideo: LocalizedString
}

export interface BasicInfoSectionConfiguration {
    /**
     * @title Basic Info label
     * @description Label for the Basic Info input
     * @title_de Basisinfo-Etikett
     * @description_de Beschriftung für den Basisinfo-Eingang
     */
    lblBasicInformation: LocalizedString
    /**
     * @title Company label
     * @description Label for the Company input
     * @title_de Firmenlabel
     * @description_de Label für die Unternehmenseingabe
     */
    lblCompany: LocalizedString
    /**
     * @title Company placeholder
     * @description Placeholder for the Company input
     * @title_de Platzhalter für Unternehmen
     * @description_de Platzhalter für die Unternehmenseingabe
     */
    hntCompany: LocalizedString
    /**
     * @title Synonyms label
     * @description Label for the Synonyms input
     * @title_de Synonyme Label
     * @description_de Bezeichnung für die Synonym-Eingabe
     */
    lblSynonyms: LocalizedString
    /**
     * @title Synonyms placeholder
     * @description Placeholder for the Synonyms input
     * @title_de Synonyme Platzhalter
     * @description_de Platzhalter für die Synonyms-Eingabe
     */
    hntSynonyms: LocalizedString
    /**
     * @title Industry label
     * @description Label for the Industry input
     * @title_de Branchenlabel
     * @description_de Label für den Industry Input
     */
    lblIndustry: LocalizedString
    /**
     * @title Industry placeholder
     * @description Placeholder for the Industry input
     * @title_de Branchenplatzhalter
     * @description_de Platzhalter für die Brancheneingabe
     */
    hntIndustry: LocalizedString
    /**
     * @title Phone label
     * @description Label for the Phone input
     * @title_de Telefonetikett
     * @description_de Beschriftung für den Telefoneingang
     */
    lblPhoneNumber: LocalizedString
    /**
     * @title Phone placeholder
     * @description Placeholder for the Phone input
     * @title_de Telefon-Platzhalter
     * @description_de Platzhalter für die Telefoneingabe
     */
    hntPhoneNumber: LocalizedString
    /**
     * @title Mobile label
     * @description Label for the Mobile input
     * @title_de Handy-Label
     * @description_de Label für den Mobile-Eingang
     */
    lblMobile: LocalizedString
    /**
     * @title Mobile placeholder
     * @description Placeholder for the Mobile input
     * @title_de Platzhalter für Mobilgeräte
     * @description_de Platzhalter für die Mobile-Eingabe
     */
    hntMobile: LocalizedString
    /**
     * @title Fax label
     * @description Label for the Fax input
     * @title_de Faxetikett
     * @description_de Label für den Faxeingang
     */
    lblFax: LocalizedString
    /**
     * @title Fax placeholder
     * @description Placeholder for the Fax input
     * @title_de Fax-Platzhalter
     * @description_de Platzhalter für die Faxeingabe
     */
    hntFax: LocalizedString
    /**
     * @title Email label
     * @description Label for the Email input
     * @title_de E-Mail-Label
     * @description_de Label für die E-Mail-Eingabe
     */
    lblEmail: LocalizedString
    /**
     * @title Email placeholder
     * @description Placeholder for the Email input
     * @title_de E-Mail-Platzhalter
     * @description_de Platzhalter für die E-Mail-Eingabe
     */
    hntEmail: LocalizedString
    /**
     * @title Email2 label
     * @description Label for the Email2 input
     * @title_de E-Mail2-Label
     * @description_de Label für die E-Mail2-Eingabe
     */
    lblEmail2: LocalizedString
    /**
     * @title Email2 placeholder
     * @description Placeholder for the Email2 input
     * @title_de E-Mail2-Platzhalter
     * @description_de Platzhalter für die E-Mail2-Eingabe
     */
    hntEmail2: LocalizedString
    /**
     * @title Website label
     * @description Label for the Website input
     * @title_de Website-Label
     * @description_de Label für die Website-Eingabe
     */
    lblWebsite: LocalizedString
    /**
     * @title Website placeholder
     * @description Placeholder for the Website input
     * @title_de Website-Platzhalter
     * @description_de Platzhalter für die Website-Eingabe
     */
    hntWebsite: LocalizedString
    /**
     * @title Address1 label
     * @description Label for the Address1 input
     * @title_de Adressetikett1
     * @description_de Beschriftung für die Adresseingabe1
     */
    lblAddress1: LocalizedString
    /**
     * @title Address1 placeholder
     * @description Placeholder for the Address1 input
     * @title_de Adressplatzhalter1
     * @description_de Platzhalter für die Adresseingabe1
     */
    hntAddress1: LocalizedString
    /**
     * @title Address2 label
     * @description Label for the Address2 input
     * @title_de Adressetikett2
     * @description_de Beschriftung für die Adresseingabe2
     */
    lblAddress2: LocalizedString
    /**
     * @title Address2 placeholder
     * @description Placeholder for the Address2 input
     * @title_de Adressplatzhalter2
     * @description_de Platzhalter für die Adresseingabe2
     */
    hntAddress2: LocalizedString
    /**
     * @title Address3 label
     * @description Label for the Address3 input
     * @title_de Adressetikett3
     * @description_de Beschriftung für die Adresseingabe3
     */
    lblAddress3: LocalizedString
    /**
     * @title Address3 placeholder
     * @description Placeholder for the Address3 input
     * @title_de Adressplatzhalter3
     * @description_de Platzhalter für die Adresseingabe3
     */
    hntAddress3: LocalizedString
    /**
     * @title Zip Code label
     * @description Label for the Zip Code input
     * @title_de Plz-Code-Etikett
     * @description_de Beschriftung für die Postleitzahleingabe
     */
    lblZipCode: LocalizedString
    /**
     * @title Zip Code placeholder
     * @description Placeholder for the Zip Code input
     * @title_de Plz-Platzhalter
     * @description_de Platzhalter für die Postleitzahleingabe
     */
    hntZipCode: LocalizedString
    /**
     * @title City label
     * @description Label for the City input
     * @title_de Stadtlabel
     * @description_de Label für die Eingabe "Stadt"
     */
    lblCity: LocalizedString
    /**
     * @title City placeholder
     * @description Placeholder for the City input
     * @title_de Platzhalter "Stadt"
     * @description_de  Platzhalter für die City-Eingabe
     */
    hntCity: LocalizedString
    /**
     * @title Country label
     * @description Label for the Country input
     * @title_de Länderlabel
     * @description_de Bezeichnung für die Ländereingabe
     */
    lblCountry: LocalizedString
    /**
     * @title Country placeholder
     * @description Placeholder for the Country input
     * @title_de Platzhalter für Land
     * @description_de Platzhalter für die Ländereingabe
     */
    hntCountry: LocalizedString
    /**
     * @title Company Size label
     * @description Label for the Company Size input
     * @title_de Firmengrößenlabel
     * @description_de Beschriftung für die Eingabe der Unternehmensgröße
     */
    lblCompanySize: LocalizedString
    /**
     * @title Company Size placeholder
     * @description Placeholder for the Company Size input
     * @title_de Platzhalter "Unternehmensgröße"
     * @description_de Platzhalter für die Eingabe "Unternehmensgröße"
     */
    hntCompanySize: LocalizedString
    /**
     * @title Founding year label
     * @description Label for the Founding year input
     * @title_de Label für das Gründungsjahr
     * @description_de Label für den Input des Gründungsjahres
     */
    lblFoundingYear: LocalizedString
    /**
     * @title Founding year placeholder
     * @description Placeholder for the Founding year input
     * @title_de Platzhalter für das Gründungsjahr
     * @description_de Platzhalter für die Eingabe des Gründungsjahres
     */
    hntFoundingYear: LocalizedString
}

export interface BasicInfoSectionConfigurationReadonly {
    /**
     * @title Company name readonly
     * @description Readonly for the Name input
     * @title_de Firmenname schreibgeschützt
     * @description_de Nur Lesezugriff für die Eingabe von Name
     */
    companyNameReadonly: boolean
    /**
     * @title Phone Number Readonly
     * @description Readonly for the Phone Number input
     * @title_de Telefonnummer Schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Rufnummer
     */
    phoneNumberReadonly: boolean
    /**
     * @title Mobile Number Readonly
     * @description Readonly for the Mobile Number input
     * @title_de Handynummer Schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Handynummer
     */
    mobileNumberReadonly: boolean
    /**
     * @title Fax Readonly
     * @description Readonly for the Fax input
     * @title_de Fax schreibgeschützt
     * @description_de Schreibgeschützt für den Fax-Eingang
     */
    faxReadonly: boolean
    /**
     * @title Email Readonly
     * @description Readonly for the Email input
     * @title_de E-Mail schreibgeschützt
     * @description_de Schreibgeschützt für die E-Mail-Eingabe
     */
    emailReadonly: boolean
    /**
     * @title Website Readonly
     * @description Readonly for the Website input
     * @title_de Website schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Website
     */
    websiteReadonly: boolean
    /**
     * @title Address1 Readonly
     * @description Readonly for the Address1 input
     * @title_de Address1 schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Address1
     */
    address1Readonly: boolean
    /**
     * @title Address2 Readonly
     * @description Readonly for the Address2 input
     * @title_de Address2 schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Address2
     */
    address2Readonly: boolean
    /**
     * @title Address3 Readonly
     * @description Readonly for the Address3 input
     * @title_de Address3 schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Address3
     */
    address3Readonly: boolean
    /**
     * @title ZipCode Readonly
     * @description Readonly for the ZipCode input
     * @title_de ZipCode schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der ZipCode
     */
    zipCodeReadonly: boolean
    /**
     * @title City Readonly
     * @description Readonly for the City input
     * @title_de City schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der City
     */
    cityReadonly: boolean
    /**
     * @title Country Readonly
     * @description Readonly for the Country input
     * @title_de Country schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Country
     */
    countryReadonly: boolean
    /**
     * @title Copmany Size Readonly
     * @description Readonly for the Copmany Size input
     * @title_de Copmany Size schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Copmany Size
     */
    companySizeReadonly: boolean
    /**
     * @title Industry Readonly
     * @description Readonly for the Industry input
     * @title_de Industry schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der Industry
     */
    industryReadonly: boolean
    /**
     * @title FoundingYear Readonly
     * @description Readonly for the FoundingYear input
     * @title_de FoundingYear schreibgeschützt
     * @description_de Schreibgeschützt für die Eingabe der FoundingYear
     */
    foundingYearReadonly: boolean
}

export interface BasicInfoSectionConfigurationMandatory {
    /**
     * @title Company name mandatory
     * @description Mandatory for the Name input
     * @title_de Firmenname obligatorisch
     * @description_de Obligatorisch für die Eingabe des Namens
     */
    companyNameMandatory: boolean
    /**
     * @title Phone Number Mandatory
     * @description Mandatory for the Phone Number input
     * @title_de Telefonnummer Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Rufnummer
     */
    phoneNumberMandatory: boolean
    /**
     * @title Mobile Number Mandatory
     * @description Mandatory for the Mobile Number input
     * @title_de Handynummer Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Handynummer
     */
    mobileNumberMandatory: boolean
    /**
     * @title Fax Mandatory
     * @description Mandatory for the Fax input
     * @title_de Fax Obligatorisch
     * @description_de Obligatorisch für den Fax-Eingang
     */
    faxMandatory: boolean
    /**
     * @title Email Mandatory
     * @description Mandatory for the Email input
     * @title_de E-Mail Obligatorisch
     * @description_de Obligatorisch für die E-Mail-Eingabe
     */
    emailMandatory: boolean
    /**
     * @title Website Mandatory
     * @description Mandatory for the Website input
     * @title_de Website Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Website
     */
    websiteMandatory: boolean
    /**
     * @title Address1 Mandatory
     * @description Mandatory for the Address1 input
     * @title_de Address1 Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Address1
     */
    address1Mandatory: boolean
    /**
     * @title Address2 Mandatory
     * @description Mandatory for the Address2 input
     * @title_de Address2 Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Address2
     */
    address2Mandatory: boolean
    /**
     * @title Address3 Mandatory
     * @description Mandatory for the Address3 input
     * @title_de Address3 Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Address3
     */
    address3Mandatory: boolean
    /**
     * @title ZipCode Mandatory
     * @description Mandatory for the ZipCode input
     * @title_de ZipCode Obligatorisch
     * @description_de Obligatorisch für die Eingabe der ZipCode
     */
    zipCodeMandatory: boolean
    /**
     * @title City Mandatory
     * @description Mandatory for the City input
     * @title_de City Obligatorisch
     * @description_de Obligatorisch für die Eingabe der City
     */
    cityMandatory: boolean
    /**
     * @title Country Mandatory
     * @description Mandatory for the Country input
     * @title_de Country Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Country
     */
    countryMandatory: boolean
    /**
     * @title Copmany Size Mandatory
     * @description Mandatory for the Copmany Size input
     * @title_de Copmany Size Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Copmany Size
     */
    companySizeMandatory: boolean
    /**
     * @title Industry Mandatory
     * @description Mandatory for the Industry input
     * @title_de Industry Obligatorisch
     * @description_de Obligatorisch für die Eingabe der Industry
     */
    industryMandatory: boolean
    /**
     * @title FoundingYear Mandatory
     * @description Mandatory for the FoundingYear input
     * @title_de FoundingYear Obligatorisch
     * @description_de Obligatorisch für die Eingabe der FoundingYear
     */
    foundingYearMandatory: boolean
}

export interface DescriptionSectionConfiguration {
    /**
     * @title Description label
     * @description Label for the Description input
     * @title_de Beschreibungsetikett
     * @description_de Label für den Beschreibungseingang
     */
    lblDescription: LocalizedString
    /**
     * @title Language label
     * @description Label for the Language input
     * @title_de Sprachbezeichnung
     * @description_de Bezeichnung für die Spracheingabe
     */
    lblLanguage: LocalizedString
    /**
     * @title Language placeholder
     * @description Label for the Language input
     * @title_de Platzhalter für Sprache
     * @description_de Bezeichnung für die Spracheingabe
     */
    hntLanguage: LocalizedString
    /**
     * @title Long Description label
     * @description Label for the Long Description input
     * @title_de Lange Beschreibungsbezeichnung
     * @description_de Beschriftung für die Eingabe "Lange Beschreibung"
     */
    lblLongDescription: LocalizedString
    /**
     * @title Long description placeholder
     * @description Placeholder for the Long Description input
     * @title_de Platzhalter mit langer Beschreibung
     * @description_de Platzhalter für die Eingabe "Lange Beschreibung"
     */
    hntLongDescription: LocalizedString
    /**
     * @title Short Description label
     * @description Label for the Short Description input
     * @title_de Kurzbeschreibungsetikett
     * @description_de Beschriftung für die Eingabe "Kurzbeschreibung"
     */
    lblShortDescription: LocalizedString
    /**
     * @title Short description placeholder
     * @description Placeholder for the Short Description input
     * @title_de Kurzbeschreibung Platzhalter
     * @description_de Platzhalter für die Kurzbeschreibungseingabe
     */
    hntShortDescription: LocalizedString
    /**
     * @title Add Translation Button
     * @description Label for the Button Add Translation
     * @title_de Schaltfläche "Übersetzung hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Übersetzung hinzufügen
     */
    btnAddTranslation: LocalizedString
}

export interface DescriptionSectionConfigurationMandatory {
    /**
     * @title Company long description mandatory
     * @description Mandatory for the long description input
     * @title_de Pflichtfeld für die ausführliche Unternehmensbeschreibung
     * @description_de Pflichtfeld für die Eingabe der ausführlichen Unternehmensbeschreibung
     */
    companyLongDescriptionMandatory: boolean
    /**
     * @title Company short description mandatory
     * @description Mandatory for the short description input
     * @title_de Pflichtfeld für die kurze Unternehmensbeschreibung
     * @description_de Pflichtfeld für die Eingabe der kurzen Unternehmensbeschreibung
     */
    companyShortDescriptionMandatory: boolean
}

export interface KeywordsSectionConfiguration {
    /**
     * @title Keywords title
     * @description Title for the Keywords section
     * @title_de Titel der Schlüsselwörter
     * @description_de Titel für die Eingabe Schlüsselwörter
     */
    title: LocalizedString
    /**
     * @title Keywords label
     * @description Label for the Keywords input
     * @title_de Bezeichnung für Schlüsselwörter
     * @description_de Bezeichnung für die Eingabe Schlüsselwörter
     */
    lblKeywords: LocalizedString
    /**
     * @title Keywords label
     * @description Placeholder for the Keywords input
     * @title_de Bezeichnung für Schlüsselwörter
     * @description_de Platzhalter für die Keywords-Eingabe
     */
    hntKeywords: LocalizedString
    /**
     * @title Show "Buy more" button
     * @description Toggle visibility of "Buy more" button
     * @title_de Button „Mehr kaufen“ anzeigen
     * @description_de Sichtbarkeit der Button „Mehr kaufen“ umschalten
     */
    btnBuyMoreDisplay: boolean
    /**
     * @title "Buy more" shop link
     * @description Link for the keywords service key product in the Event Shop
     * @title_de Shop-Link „Mehr kaufen“.
     * @description_de Link zum Service-Key-Produkt „keywords“ im Event-Shop
     */
    btnBuyMoreLink: LocalizedString
}

export enum SocialMediaType {
    FACEBOOK = "FACEBOOK",
    INSTAGRAM = "INSTAGRAM",
    LINKEDIN = "LINKEDIN",
    XING = "XING",
    TWITTER = "TWITTER", //used for X, left as "Twitter" for backwards compatibility
    YOUTUBE = "YOUTUBE",
    PINTEREST = "PINTEREST",
    TIKTOK = "TIKTOK"
}

export interface SocialMediaSectionConfiguration {
    /**
     * @title Social Media label
     * @description Label for the Social Media input
     * @title_de Social-Media-Label
     * @description_de Label für den Social Media Input
     */
    lblSocialMedia: LocalizedString
    /**
     * @title Facebook label
     * @description Label for the Facebook input
     * @title_de Facebook-Label
     * @description_de Label für die Facebook-Eingabe
     */
    lblFacebook: LocalizedString
    /**
     * @title Facebook placeholder
     * @description Placeholder for the Facebook input
     * @title_de Facebook-Platzhalter
     * @description_de Platzhalter für die Facebook-Eingabe
     */
    hntFacebook: LocalizedString
    /**
     * @title Instagram label
     * @description Label for the Instagram input
     * @title_de Instagram-Label
     * @description_de Label für die Instagram-Eingabe
     */
    lblInstagram: LocalizedString
    /**
     * @title Instagram placeholder
     * @description Placeholder for the Instagram input
     * @title_de Instagram-Platzhalter
     * @description_de Platzhalter für die Instagram-Eingabe
     */
    hntInstagram: LocalizedString
    /**
     * @title LinkedIn label
     * @description Label for the LinkedIn input
     * @title_de LinkedIn-Label
     * @description_de Label für die LinkedIn-Eingabe
     */
    lblLinkedIn: LocalizedString
    /**
     * @title LinkedIn placeholder
     * @description Placeholder for the LinkedIn input
     * @title_de LinkedIn-Platzhalter
     * @description_de Platzhalter für die LinkedIn-Eingabe
     */
    hntLinkedIn: LocalizedString
    /**
     * @title Xing label
     * @description Label for the Xing input
     * @title_de Xing-Label
     * @description_de Label für die Xing-Eingabe
     */
    lblXing: LocalizedString
    /**
     * @title Xing placeholder
     * @description Placeholder for the Xing input
     * @title_de Xing-Platzhalter
     * @description_de Platzhalter für die Xing-Eingabe
     */
    hntXing: LocalizedString
    /**
     * @title X label
     * @description Label for the X input
     * @title_de X-Label
     * @description_de Label für die X-Eingabe
     */
    lblX: LocalizedString
    /**
     * @title X placeholder
     * @description Placeholder for the X input
     * @title_de X-Platzhalter
     * @description_de Platzhalter für die X-Eingabe
     */
    hntX: LocalizedString
    /**
     * @title YouTube label
     * @description Label for the YouTube input
     * @title_de YouTube-Label
     * @description_de Label für die YouTube-Eingabe
     */
    lblYouTube: LocalizedString
    /**
     * @title YouTube placeholder
     * @description Placeholder for the YouTube input
     * @title_de YouTube-Platzhalter
     * @description_de Platzhalter für die YouTube-Eingabe
     */
    hntYouTube: LocalizedString
    /**
     * @title Pinterest label
     * @description Label for the Pinterest input
     * @title_de Pinterest-Label
     * @description_de Label für die Pinterest-Eingabe
     */
    lblPinterest: LocalizedString
    /**
     * @title Pinterest placeholder
     * @description Placeholder for the Pinterest input
     * @title_de Pinterest-Platzhalter
     * @description_de Platzhalter für die Pinterest-Eingabe
     */
    hntPinterest: LocalizedString
    /**
     * @title TikTok label
     * @description Label for the TikTok input
     * @title_de TikTok-Label
     * @description_de Label für die TikTok-Eingabe
     */
    lblTikTok: LocalizedString
    /**
     * @title TikTok placeholder
     * @description Placeholder for the TikTok input
     * @title_de TikTok-Platzhalter
     * @description_de Platzhalter für die TikTok-Eingabe
     */
    hntTikTok: LocalizedString
    /**
     * @title Social media order
     * @description Order of social media links in the section, item is not shown if not listed here
     * @title_de Ordnung der Social Media
     * @description_de Reihenfolge der Social-Media-Links im Abschnitt, Element wird nicht angezeigt, wenn es hier nicht aufgeführt ist
     */
    itemsOrder: SocialMediaType[]
}

export interface iFrameSectionConfiguration {
    /**
     * @title iFrame label
     * @description Label for the iFrame  input
     * @title_de Bezeichnung
     * @description_de Bezeichnung für die iFrame-Eingabe
     */
    lbliFrame: LocalizedString
    /**
     * @title URL label
     * @description Label for the iFrame URL input
     * @title_de URL-Bezeichnung
     * @description_de Bezeichnung für die iFrame-URL-Eingabe
     */
    lbliFrameURL: LocalizedString
    /**
     * @title iFrame URL placeholder
     * @description Placeholder for the iFrame URL input
     * @title_de Platzhalter für iFrame-URL
     * @description_de Platzhalter für die iFrame-URL-Eingabe
     */
    hntiFrameUR: LocalizedString
    /**
     * @title iFrame URL DE label
     * @description Label for the iFrame URL DE input
     * @title_de iFrame URL DE Label
     * @description_de Bezeichnung für die iFrame URL DE inputl
     */
    lbliFrameURLDE: LocalizedString
    /**
     * @title iFrame URL DE placeholder
     * @description Placeholder for the iFrame URL DE input
     * @title_de Platzhalter für iFrame URL DE
     * @description_de Platzhalter für die iFrame URL DE-Eingabe
     */
    hntiFrameURLDE: LocalizedString
}

export interface ConsentContentSectionConfiguration {
    /**
     * @title Consent Content label
     * @description Label for the Consent Content input
     * @title_de Label für Einwilligungsinhalt
     * @description_de Label für die Eingabe des Einwilligungsinhalts
     */
    lblConsentContent: LocalizedString
    /**
     * @title Language label
     * @description Label for the Language input
     * @title_de Sprachbezeichnung
     * @description_de Bezeichnung für die Spracheingabe
     */
    lblLanguage: LocalizedString
    /**
     * @title Consent Title label
     * @description Label for the Consent Title input
     * @title_de Bezeichnung "Zustimmungstitel"
     * @description_de Bezeichnung für die Eingabe des Zustimmungstitels
     */
    lblConsentTitle: LocalizedString
    /**
     * @title Content Title placeholder
     * @description Placeholder for the Content Title input
     * @title_de Platzhalter für Inhaltstitel
     * @description_de Platzhalter für die Inhaltstiteleingabe
     */
    hntConsentTitle: LocalizedString
    /**
     * @title Consent Text label
     * @description Label for the Consent Text input
     * @title_de Zustimmung Textbeschriftung
     * @description_de Bezeichnung für die Eingabe des Zustimmungstextes
     */
    lblConsentText: LocalizedString
    /**
     * @title Consent Text placeholder
     * @description Placeholder for the Consent Text input
     * @title_de Platzhalter für Zustimmungstext
     * @description_de Platzhalter für die Eingabe des Zustimmungstextes
     */
    hntConsentText: LocalizedString
}

export interface LinksPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add Link Button
     * @description Label for the Button Add Link
     * @title_de Schaltfläche "Link hinzufügen"
     * @description_de Beschriftung für den Link Schaltfläche Hinzufügen
     */
    btnAddLink: LocalizedString
    /**
     * @title Add Links Section config
     * @description Label for the Add Links page entry
     * @title_de  Konfiguration des Abschnitts "Links hinzufügen"
     * @description_de  Bezeichnung für den Seiteneintrag "Links hinzufügen"
     */
    sctLink: LinkSectionConfiguration
}

export interface LinkSectionConfiguration {
    /**
     * @title Empty link title
     * @description Label for the empty link title
     * @title_de Empty link title
     * @description_de Label for the empty link title
     */
    titleEmptyLink: LocalizedString
    /**
     * @title URL label
     * @description Label for the URL input
     * @title_de URL-Bezeichnung
     * @description_de Bezeichnung für die URL-Eingabe
     */
    lblURL: LocalizedString
    /**
     * @title URL placeholder
     * @description Placeholder for the URL input
     * @title_de URL-Platzhalter
     * @description_de Platzhalter für die URL-Eingabe
     */
    hntURL: LocalizedString
    /**
     * @title Text label
     * @description Label for the Text input
     * @title_de Textbeschriftung
     * @description_de Beschriftung für die Texteingabe
     */
    lblText: LocalizedString
    /**
     * @title Text placeholder
     * @description Placeholder for the Text input
     * @title_de Textplatzhalter
     * @description_de Platzhalter für die Texteingabe
     */
    hntText: LocalizedString
}

export interface CategoriesPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
    /**
     * @title Expanded root categories
     * @description List of IDs of root categories that are expanded by default
     * @title_de Erweiterte Rootkategorien
     * @description_de Liste der IDs der Stammkategorien, die standardmäßig erweitert werden
     */
    expandedRootCategories: string[]
}

export interface LicencesPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
}

export interface ProductsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add Product Button
     * @description Label for the Button Add Product
     * @title_de Schaltfläche "Produkt hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Produkt hinzufügen
     */
    btnAddProduct: LocalizedString
    /**
     * @title Add Product Section config
     * @description Label for the Add Product profile page entry
     * @title_de  Produktabschnittskonfiguration hinzufügen
     * @description_de Bezeichnung für den Seiteneintrag Produktprofil hinzufügen
     */
    sctProduct: ProductSectionConfiguration
}

export interface ProductsForCollectionsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add Product Button
     * @description Label for the Button Add Product
     * @title_de Schaltfläche "Produkt hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Produkt hinzufügen
     */
    btnAddProduct: LocalizedString
    /**
     * @title Collection categories root ID
     * @description ID of the root element for collection categories
     * @title_de Root-ID der Sammlungskategorien
     * @description_de ID des Stammelements für Sammlungskategorien
     */
    collectionCategoriesRootId: string
}

export interface ProductSectionConfiguration {
    /**
     * @title Empty product title
     * @description Label for the empty Product title
     * @title_de Empty product title
     * @description_de Label for the empty Product title
     */
    titleEmptyProduct: LocalizedString
    /**
     * @title Product image label
     * @description Label for the Product image
     * @title_de Produktbild-Etikett
     * @description_de Etikett für das Produktbild
     */
    lblProductImage: LocalizedString
    /**
     * @title Product image message label
     * @description Label for the Product image message label input
     * @title_de Etikett der Produktbildnachricht
     * @description_de Etikett für die Eingabe der Produktbildnachricht
     */
    lblProductImageMessage: LocalizedString
    /**
     * @title Product background upload visible
     * @description Toggle the visibility of product background upload
     * @title_de Produkthintergrund-Upload sichtbar
     * @description_de Schalten Sie die Sichtbarkeit des Produkthintergrund-Uploads um
     */
    showProductBackgroundUpload: boolean
    /**
     * @title Product Background placeholder
     * @description Label for the Product Background input
     * @title_de Platzhalter "Produkthintergrund"
     * @description_de Beschriftung für die Eingabe "Produkthintergrund"
     */
    lblProductBackground: LocalizedString
    /**
     * @title Product Image Title label
     * @description Label for the Product Image Title input
     * @title_de Titelbezeichnung des Produktbilds
     * @description_de Beschriftung für die Eingabe des Produktbildtitels
     */
    hntTitle: LocalizedString
    /**
     * @title Product Background Title label
     * @description Label for the Product Background Title input
     * @title_de Titelbezeichnung des phintergrundes
     * @description_de Beschriftung für die Eingabe des Produkthintergrundtitels
     */
    hntTitleBackground: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Produktbilds
     * @description_de Bezeichnung für die Eingabe "Produktbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title Product Background Properties placeholder
     * @description Label for the Product Background Properties input
     * @title_de Eigenschaften des Produkthintergrund
     * @description_de Bezeichnung für die Eingabe "Produkthintergrundeigenschaften"
     */
    hntPropBackground: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Logo Browse files Button
     * @description Label for the Button Browse files
     * @title_de Logo Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFilesBackground: LocalizedString
    /**
     * @title Logo Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Logo Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImgBackground: LocalizedString
    /**
     * @title Product name label
     * @description Label for the Product name label input
     * @title_de  Etikett des Produktnamens
     * @description_de Etikett für die Eingabe des Produktnamensetiketts
     */
    lblProductname: LocalizedString
    /**
     * @title Product name placeholder
     * @description Placeholder for the Product name input
     * @title_de Platzhalter für Produktname
     * @description_de Platzhalter für die Eingabe des Produktnamens
     */
    hntProductname: LocalizedString
    /**
     * @title Start date label
     * @description Label for the Start date input
     * @title_de Etikett mit Startdatum
     * @description_de Etikett für die Eingabe des Startdatums
     */
    lblProductPublicationStart: LocalizedString
    /**
     * @title Start date placeholder
     * @description Placeholder for the Start date
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    hntProductPublicationStart: LocalizedString
    /**
     * @title End date label
     * @description Label for the End date input
     * @title_de Etikett mit Enddatum
     * @description_de Etikett für die Eingabe des Enddatums
     */
    lblProductPublicationEnd: LocalizedString
    /**
     * @title End date plaecholder
     * @description Placeholder for the End date
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    hntProductPublicationEnd: LocalizedString
    /**
     * @title Description for the detail page label
     * @description Label for the Description for the detail page label input
     * @title_de Beschreibung für die Detailseitenbeschriftung
     * @description_de Beschriftung für die Beschreibung für die Detailseitenbeschriftungseingabe
     */
    lblDescriptionDetail: LocalizedString
    /**
     * @title Description for the detail page placeholder
     * @description Placeholder for the Description for the detail page input
     * @title_de Beschreibung für den Platzhalter der Detailseite
     * @description_de Platzhalter für die Beschreibung für die Detailseiteneingabe
     */
    hntDescriptionDetail: LocalizedString
    /**
     * @title Short description for the list view label
     * @description Label for the Short description for the list view label input
     * @title_de Kurzbeschreibung für die Listenansichtsbeschriftung
     * @description_de Beschriftung für die Kurzbeschreibung für die Listenansichtsbeschriftungseingabe
     */
    lblDescriptionShort: LocalizedString
    /**
     * @title Short description for the list view placeholder
     * @description Placeholder Short description for the list view input
     * @title_de Kurzbeschreibung für den Platzhalter der Listenansicht
     * @description_de Platzhalter Kurzbeschreibung für die Listenansichtseingabe
     */
    hntDescriptionShort: LocalizedString
    /**
     * @title Product Information label
     * @description Label for the Product Information input
     * @title_de Produktinformationsetikett
     * @description_de Etikett für die Eingabe der Produktinformation
     */
    lblTabProductInformation: LocalizedString
    /**
     * @title Links label
     * @description Label for the Links input
     * @title_de Links Label
     * @description_de Bezeichnung für die Eingabe Links
     */
    lblTabLinks: LocalizedString
    /**
     * @title Categories label
     * @description Label for the Categories input
     * @title_de Kategorien Label
     * @description_de Bezeichnung für die Eingabe Kategorien
     */
    lblTabCategories: LocalizedString
    /**
     * @title Multimedia label
     * @description Label for the Multimedia input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblTabMultimedia: LocalizedString
    /**
     * @title Keywords label
     * @description Label for the Keywords input
     * @title_de Bezeichnung für Schlüsselwörter
     * @description_de Bezeichnung für die Eingabe Schlüsselwörter
     */
    lblTabKeywords: LocalizedString
    /**
     * @title Product Information Tab Description
     * @description Label for Product Information Tab Description
     * @title_de Registerkarte Produktinformation Beschreibung
     * @description_de Etikett für die Registerkarte Produktinformation Beschreibung
     */
    lblProductInformationDescription: LocalizedString
    /**
     * @title Links Tab Description
     * @description Label for Links Tab Description
     * @title_de Links Tab Description
     * @description_de Label for Links Tab Description
     */
    lblLinksDescription: LocalizedString
    /**
     * @title Categories Tab Description
     * @description Label for Categories Tab Description
     * @title_de Categories Tab Description
     * @description_de Label for Categories Tab Description
     */
    lblCategoriesDescription: LocalizedString
    /**
     * @title Multimedia Tab Description
     * @description Label for Multimedia Tab Description
     * @title_de Multimedia Tab Description
     * @description_de Label for Multimedia Tab Description
     */
    lblMultimediaDescription: LocalizedString
    /**
     * @title Multimedia section configuration
     * @description Labels for the Multimedia section
     * @title_de Konfiguration der Multimedia-Bereich
     * @description_de Beschriftungen für die Multimedia-Bereich
     */
    multimediaSection: MultimediaImageSectionConfiguration
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
    /**
     * @title Multimedia files limit
     * @description Maximum number of files one can upload in the Multimedia tab
     * @title_de --
     * @description_de --
     */
    multimediaLimit: number
    /**
     * @title Product Type label
     * @description Label for Product Type field Description
     * @title_de --
     * @description_de --
     */
    productType: LocalizedString
    /**
     * @title Product Type placeholder
     * @description Product Type field placeholder
     * @title_de --
     * @description_de --
     */
    productTypePlaceholder: LocalizedString
    /**
     * @title Product property Value label
     * @description Product property Value field label
     * @title_de --
     * @description_de --
     */
    lblPropertyValue: LocalizedString
    /**
     * @title Product property value placeholder
     * @description Product property value field placeholder
     * @title_de --
     * @description_de --
     */
    propertyValuePlaceholder: LocalizedString

    /**
     * @title Product type change modal title
     * @description Title of the modal that is shown when the product type is changed
     * @title_de  Modaler Titel zum Ändern des Produkttyps
     * @description_de Titel des Modals, der angezeigt wird, wenn der Produkttyp geändert wird
     */
    lblProductTypeChangeModalTitle: LocalizedString
    /**
     * @title Product type change modal title
     * @description Title of the modal that is shown when the product type is changed
     * @title_de Modaler Text zum Ändern des Produkttyps
     * @description_de Text des Modals, der angezeigt wird, wenn der Produkttyp geändert wird
     */
    lblProductTypeChangeModalText: LocalizedString
    /**
     * @title Create category binding when product type is selected
     * @description Enable an option to create a binding to a category selected as a product type
     * @title_de Kategoriebindung erstellen, wenn Produkttyp ausgewählt ist
     * @description_de Option aktivieren, um eine Bindung zu einer ausgewählten Kategorie als Produkttyp zu erstellen
     */
    categoryBindingForProductType: boolean
    /**
     * @title Product Collection label
     * @description Label for Product Collection field
     * @title_de Produktsammlung Label
     * @description_de Beschriftung für das Feld „Produktsammlung“
     */
    lblProductCollection: LocalizedString
    /**
     * @title Product Collection placeholder
     * @description Placeholder for Product Collection field
     * @title_de Produktsammlung Platzhalter
     * @description_de Platzhalter für das Feld „Produktsammlung“
     */
    hntProductCollection: LocalizedString
}

export interface TrademarksPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add Trademark Button
     * @description Label for the Button Add Trademark
     * @title_de Schaltfläche "Marke hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Marke hinzufügen
     */
    btnAddTrademark: LocalizedString
    /**
     * @title Add Trademark Section config
     * @description Label for the Add Trademark profile page entry
     * @title_de Konfiguration des Markenabschnitts hinzufügen
     * @description_de Label für den Eintrag auf der Profilseite Marke hinzufügen
     */
    sctTrademark: TrademarkSectionConfiguration
}

export interface TrademarkSectionConfiguration {
    /**
     * @title Empty Trademark title
     * @description Label for the empty Trademark title
     * @title_de Empty Trademark title
     * @description_de Label for the empty Trademark title
     */
    titleEmptyTrademark: LocalizedString
    /**
     * @title Trademark image label
     * @description Label for the Trademark image
     * @title_de Markenbildlabel
     * @description_de Label für das Markenbild
     */
    lblTrademarkImage: LocalizedString
    /**
     * @title Trademark image message label
     * @description Label for the Trademark image message label input
     * @title_de Label für Markenbildnachrichten
     * @description_de Label für die Label-Eingabe für Markenbildnachrichten
     */
    lblTrademarkImageMessage: LocalizedString
    /**
     * @title Company Image Title label
     * @description Label for the Company Image Title input
     * @title_de Titelbezeichnung des Firmenbilds
     * @description_de Beschriftung für die Eingabe des Unternehmensbildtitels
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Trademark name label
     * @description Label for the Trademark name label input
     * @title_de  Markennamensetikett
     * @description_de Label für die Eingabe des Markennamen-Labels
     */
    lblTrademarkname: LocalizedString
    /**
     * @title Trademark name placeholder
     * @description Placeholder for the Trademark name input
     * @title_de Platzhalter für Markennamen
     * @description_de Platzhalter für die Eingabe des Markennamens
     */
    hntTrademarkname: LocalizedString
    /**
     * @title Description for the detail page label
     * @description Label for the Description for the detail page label input
     * @title_de Beschreibung für die Detailseitenbeschriftung
     * @description_de Beschriftung für die Beschreibung für die Detailseitenbeschriftungseingabe
     */
    lblDescriptionDetail: LocalizedString
    /**
     * @title Description for the detail page placeholder
     * @description Placeholder for the Description for the detail page input
     * @title_de Beschreibung für den Platzhalter der Detailseite
     * @description_de Platzhalter für die Beschreibung für die Detailseiteneingabe
     */
    hntDescriptionDetail: LocalizedString
    /**
     * @title Short description for the list view label
     * @description Label for the Short description for the list view label input
     * @title_de Kurzbeschreibung für die Listenansichtsbeschriftung
     * @description_de Beschriftung für die Kurzbeschreibung für die Listenansichtsbeschriftungseingabe
     */
    lblDescriptionShort: LocalizedString
    /**
     * @title Short description for the list view placeholder
     * @description Placeholder Short description for the list view input
     * @title_de Kurzbeschreibung für den Platzhalter der Listenansicht
     * @description_de Platzhalter Kurzbeschreibung für die Listenansichtseingabe
     */
    hntDescriptionShort: LocalizedString
    /**
     * @title Categories label
     * @description Label for the Categories input
     * @title_de Kategorien Label
     * @description_de Bezeichnung für die Eingabe Kategorien
     */
    lblTabCategories: LocalizedString
    /**
     * @title Multimedia label
     * @description Label for the Multimedia input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblTabMultimedia: LocalizedString
    /**
     * @title Keywords label
     * @description Label for the Keywords input
     * @title_de Bezeichnung für Schlüsselwörter
     * @description_de Bezeichnung für die Eingabe Schlüsselwörter
     */
    lblTabKeywords: LocalizedString
    /**
     * @title Categories Tab Description
     * @description Label for Categories Tab Description
     * @title_de Categories Tab Description
     * @description_de Label for Categories Tab Description
     */
    lblCategoriesDescription: LocalizedString
    /**
     * @title Multimedia Tab Description
     * @description Label for Multimedia Tab Description
     * @title_de Multimedia Tab Description
     * @description_de Label for Multimedia Tab Description
     */
    lblMultimediaDescription: LocalizedString
    /**
     * @title Multimedia section configuration
     * @description Labels for the Multimedia section
     * @title_de Konfiguration der Multimedia-Bereich
     * @description_de Beschriftungen für die Multimedia-Bereich
     */
    multimediaSection: MultimediaImageSectionConfiguration
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
    /**
     * @title Multimedia files limit
     * @description Maximum number of files one can upload in the Multimedia tab
     * @title_de --
     * @description_de --
     */
    multimediaLimit: number
}

export interface TeamPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add Staff Button
     * @description Label for the Button Add Staff
     * @title_de Schaltfläche „Personal hinzufügen“
     * @description_de Beschriftung für die Schaltfläche Notensystem hinzufügen
     */
    btnAddSPerson: LocalizedString
    /**
     * @title Add Staff Section config
     * @description Label for the Add Staff profile page entry
     * @title_de Konfiguration des Personalbereichs hinzufügen
     * @description_de Bezeichnung für den Profilseiteneintrag Mitarbeiter hinzufügen
     */
    sctPerson: PersonSectionConfiguration
}

export interface PersonSectionConfiguration {
    /**
     * @title Empty Person title
     * @description Label for the empty Person title
     * @title_de Empty Person title
     * @description_de Label for the empty Person title
     */
    titleEmptyPerson: LocalizedString
    /**
     * @title Profile image label
     * @description Label for the Profile image
     * @title_de Profilbild-Etikett
     * @description_de Label für das Profilbild
     */
    lblProfileImage: LocalizedString
    /**
     * @title Profile image message label
     * @description Label for the Trademark image message label input
     * @title_de Label für Markenbildnachrichten
     * @description_de Label für die Label-Eingabe für Markenbildnachrichten
     */
    lblProfileImageMessage: LocalizedString
    /**
     * @title Company Image Title label
     * @description Label for the Company Image Title input
     * @title_de Titelbezeichnung des Firmenbilds
     * @description_de Beschriftung für die Eingabe des Unternehmensbildtitels
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Title label
     * @description Label for the Title label input
     * @title_de  Titellabel
     * @description_de Label für die Titellabel-Eingabe
     */
    lblTitleTeam: LocalizedString
    /**
     * @title  Title name placeholder
     * @description Placeholder for the Title name input
     * @title_de Platzhalter für den Titelnamen
     * @description_de Platzhalter für die Eingabe des Titelnamens
     */
    hntTitleTeam: LocalizedString
    /**
     * @title Last name label
     * @description Label for the Last namelabel input
     * @title_de  Nachname-Etikett
     * @description_de Label für die Eingabe des Nachnamenlabels
     */
    lblLastName: LocalizedString
    /**
     * @title  Last name placeholder
     * @description Placeholder for the Last name input
     * @title_de Platzhalter für den Nachnamen
     * @description_de Platzhalter für die Eingabe des Nachnamens
     */
    hntLastName: LocalizedString
    /**
     * @title First name label
     * @description Label for the First name label input
     * @title_de  Etikett mit Vornamen
     * @description_de Beschriftung für die Beschriftungseingabe Vorname
     */
    lblFirstName: LocalizedString
    /**
     * @title  First name placeholder
     * @description Placeholder for the First name input
     * @title_de Platzhalter für den Vornamen
     * @description_de Platzhalter für die Eingabe des Vornamens
     */
    hntFirstName: LocalizedString
    /**
     * @title Role for this event label
     * @description Label for the Role for this event label input
     * @title_de  Rolle für dieses Ereignislabel
     * @description_de Label für die Rolle für diese Ereignislabeleingabe
     */
    lblRole: LocalizedString
    /**
     * @title  Role for this event dropdown
     * @description Dropdown for Role for this event
     * @title_de Rolle für dieses Ereignis-Dropdown-Menü
     * @description_de Dropdown für Rolle für dieses Ereignis
     */
    hntRole: LocalizedString
    /**
     * @title Position label
     * @description Label for the Position label input
     * @title_de  Positionsbezeichnung
     * @description_de Label für die Positionslabel-Eingabe
     */
    lblPosition: LocalizedString
    /**
     * @title  Position placeholder
     * @description Placeholder for the Position input
     * @title_de Platzhalter positionieren
     * @description_de Platzhalter für die Positionseingabe
     */
    hntPosition: LocalizedString
    /**
     * @title Company label
     * @description Label for the Company label input
     * @title_de  Firmenlabel
     * @description_de Label für die Eingabe des Firmenlabels
     */
    lblCompany: LocalizedString
    /**
     * @title  Company placeholder
     * @description Placeholder for the Company input
     * @title_de Platzhalter für Unternehmen
     * @description_de Platzhalter für die Firmeneingabe
     */
    hntCompany: LocalizedString
    /**
     * @title Phone label
     * @description Label for the Phone label input
     * @title_de  Telefonetikett
     * @description_de Label für die Eingabe des Telefonlabels
     */
    lblPhone: LocalizedString
    /**
     * @title  Phone placeholder
     * @description Placeholder for the Phone input
     * @title_de Telefon-Platzhalter
     * @description_de Platzhalter für die Telefoneingabe
     */
    hntPhone: LocalizedString
    /**
     * @title Email label
     * @description Label for the Email label input
     * @title_de  E-Mail-Label
     * @description_de Label für die E-Mail-Label-Eingabe
     */
    lblEmail: LocalizedString
    /**
     * @title  Email placeholder
     * @description Placeholder for the Email input
     * @title_de E-Mail-Platzhalter
     * @description_de Platzhalter für die E-Mail-Eingabe
     */
    hntEmail: LocalizedString
    /**
     * @title Description for the detail page label
     * @description Label for the Description for the detail page label input
     * @title_de Beschreibung für die Detailseitenbeschriftung
     * @description_de Beschriftung für die Beschreibung für die Detailseitenbeschriftungseingabe
     */
    lblDescriptionDetail: LocalizedString
    /**
     * @title Description for the detail page placeholder
     * @description Placeholder for the Description for the detail page input
     * @title_de Beschreibung für den Platzhalter der Detailseite
     * @description_de Platzhalter für die Beschreibung für die Detailseiteneingabe
     */
    hntDescriptionDetail: LocalizedString
    /**
     * @title Short description for the list view label
     * @description Label for the Short description for the list view label input
     * @title_de Kurzbeschreibung für die Listenansichtsbeschriftung
     * @description_de Beschriftung für die Kurzbeschreibung für die Listenansichtsbeschriftungseingabe
     */
    lblDescriptionShort: LocalizedString
    /**
     * @title Short description for the list view placeholder
     * @description Placeholder Short description for the list view input
     * @title_de Kurzbeschreibung für den Platzhalter der Listenansicht
     * @description_de Platzhalter Kurzbeschreibung für die Listenansichtseingabe
     */
    hntDescriptionShort: LocalizedString
    /**
     * @title Phone visible label
     * @description Label for the Phone visible checkbox
     * @title_de  Phone visible label
     * @description_de Label for the Phone visible checkbox
     */
    lblPhoneVisibleOnProfile: LocalizedString
    /**
     * @title Email visible label
     * @description Label for the Email visible checkbox
     * @title_de  Email visible label
     * @description_de Label for the Email visible checkbox
     */
    lblEmailVisibleOnProfile: LocalizedString
    /**
     * @title Person functions Whitelist
     * @description List of person function ids for contact persons
     * @title_de Kategorie Whitelist
     * @description_de Liste der Personenfunktions-IDs für für Ansprechpartner
     */
    personFunctionWhitelist: string[]
}

export interface MultimediaPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Multimedia Section config
     * @description Label for the Multimedia section page entry
     * @title_de Konfiguration des Multimedia-Bereichs
     * @description_de Label für den Seiteneintrag Multimedia-Bereich
     */
    sctMultimediaImage: MultimediaImageSectionConfiguration
}

export interface MultimediaImageSectionConfiguration {
    /**
     * @title Title
     * @description Title for the input
     * @title_de Titel
     * @description_de Titel für die Eingabe
     */
    lblDropzoneTitle: LocalizedString
    /**
     * @title Description
     * @description Description for the input
     * @title_de Beschreibung
     * @description_de Beschreibung für die Eingabe
     */
    lblDropzoneDescription: LocalizedString
    /**
     * @title Position  label
     * @description Label for the Position label input
     * @title_de Positionsbezeichnung
     * @description_de Label für die Positionslabel-Eingabe
     */
    lblHeaderPosition: LocalizedString
    /**
     * @title Preview  label
     * @description Label for the Preview label input
     * @title_de Etikettenvorschau
     * @description_de Label für die Label-Eingabe in der Vorschau
     */
    lblHeaderPreview: LocalizedString
    /**
     * @title File  label
     * @description Label for the File label input
     * @title_de Dateibezeichnung
     * @description_de Label für die Dateilabel-Eingabe
     */
    lblHeaderFile: LocalizedString
    /**
     * @title  Title placeholder
     * @description Placeholder for the Title input
     * @title_de Bildtitellabel
     * @description_de Platzhalter für die Bildtiteleingabe
     */
    hntTitle: LocalizedString
    /**
     * @title Subtitle placeholder
     * @description Placeholder for the Subtitle input
     * @title_de Untertitel-Platzhalter
     * @description_de Platzhalter für die Untertiteleingabe
     */
    hntSubtitle: LocalizedString
}

export interface ExhibitorNewsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add News Button
     * @description Label for the Button Add News
     * @title_de Schaltfläche "Neuigkeiten hinzufügen"
     * @description_de Beschriftung für den Button News hinzufügen
     */
    btnAddNews: LocalizedString
    /**
     * @title Add News Section config
     * @description Label for the Add News profile page entry (also used for Organizer News page)
     * @title_de Konfiguration des Nachrichtenbereichs hinzufügen
     * @description_de Label für den Eintrag auf der Profilseite "News hinzufügen" (wird auch für die „Organisator-News“ Seite verwendet)
     */
    sctNews: NewsSectionConfiguration
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
}

export interface OrganizerNewsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
}

export interface StoryNewsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add News Button
     * @description Label for the Button Add News
     * @title_de Schaltfläche "Neuigkeiten hinzufügen"
     * @description_de Beschriftung für den Button News hinzufügen
     */
    btnAddNews: LocalizedString
    /**
     * @title Add News Section config
     * @description Label for the Add News profile page entry
     * @title_de Konfiguration des Nachrichtenbereichs hinzufügen
     * @description_de Label für den Eintrag auf der Profilseite "News hinzufügen"
     */
    sctStory: NewsSectionConfiguration
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
}

export interface NewsSectionConfiguration {
    /**
     * @title Empty News title
     * @description Label for the empty News title
     * @title_de Empty News title
     * @description_de Label for the empty News title
     */
    titleEmptyNews: LocalizedString
    /**
     * @title News image label
     * @description Label for the News image
     * @title_de Nachrichten-Bildlabel
     * @description_de Label für das Nachrichtenbild
     */
    lblNewsImage: LocalizedString
    /**
     * @title News image message label
     * @description Label for the News image message label input
     * @title_de Nachrichtenlabel für Nachrichtenbild
     * @description_de Label für die Label-Eingabe für Nachrichten-Bildnachrichten
     */
    lblNewsImageMessage: LocalizedString
    /**
     * @title News Image Title label
     * @description Label for the News Image Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title News title label
     * @description Label for the News title label input
     * @title_de  Titellabel der Nachrichten
     * @description_de Label für die Eingabe des Titellabels für Nachrichten
     */
    lblNewsTitle: LocalizedString
    /**
     * @title  News title placeholder
     * @description Placeholder for the  News title input
     * @title_de Platzhalter für Nachrichtentitel
     * @description_de Platzhalter für die Eingabe des Nachrichtentitels
     */
    hntNewsTitle: LocalizedString
    /**
     * @title News date label
     * @description Label for the News date label input
     * @title_de  Datumsetikett für Nachrichten
     * @description_de Label für die Eingabe des News-Datumslabels
     */
    lblNewsDate: LocalizedString
    /**
     * @title  News date placeholder
     * @description Placeholder for the  News date input
     * @title_de Platzhalter für das Nachrichtendatum
     * @description_de Platzhalter für die Eingabe des Nachrichtendatums
     */
    hntNewsDate: LocalizedString
    /**
     * @title Description  label
     * @description Label for the Description  label input
     * @title_de Beschreibungsetikett
     * @description_de Beschriftung für die Beschriftungseingabe Beschreibung
     */
    lblDescription: LocalizedString
    /**
     * @title Description placeholder
     * @description Placeholder for the Description input
     * @title_de Beschreibung Platzhalter
     * @description_de Platzhalter für die Eingabe Beschreibung
     */
    hntDescription: LocalizedString
    /**
     * @title Subtitle label
     * @description Label for the Subtitle label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblSubtitle: LocalizedString
    /**
     * @title Subtitle placeholder
     * @description Placeholder Subtitle input
     * @title_de Untertitel-Platzhalter
     * @description_de Platzhalter-Untertiteleingabe
     */
    hntSubtitle: LocalizedString
    /**
     * @title Categories label
     * @description Label for the Categories input
     * @title_de Kategorien Label
     * @description_de Bezeichnung für die Eingabe Kategorien
     */
    lblTabCategories: LocalizedString
    /**
     * @title Multimedia label
     * @description Label for the Multimedia input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblTabMultimedia: LocalizedString
    /**
     * @title Keywords label
     * @description Label for the Keywords input
     * @title_de Bezeichnung für Schlüsselwörter
     * @description_de Bezeichnung für die Eingabe Schlüsselwörter
     */
    lblTabKeywords: LocalizedString
    /**
     * @title Categories Tab Description
     * @description Label for Categories Tab Description
     * @title_de Beschreibung der Registerkarte „Kategorien“
     * @description_de Bezeichnung für die Beschreibung der Registerkarte „Kategorien“
     */
    lblCategoriesDescription: LocalizedString
    /**
     * @title Multimedia Tab Description
     * @description Label for Multimedia Tab Description
     * @title_de Beschreibung der Registerkarte „Multimedia“
     * @description_de Bezeichnung für die Beschreibung der Registerkarte „Multimedia“
     */
    lblMultimediaDescription: LocalizedString
    /**
     * @title Multimedia section configuration (Exhibitor News)
     * @description Labels for the Multimedia section
     * @title_de Konfiguration der Multimedia-Bereich (Exhibitor News)
     * @description_de Beschriftungen für die Multimedia-Bereich
     */
    multimediaSectionExhibitor: MultimediaImageSectionConfiguration
    /**
     * @title Multimedia section configuration (Stories)
     * @description Labels for the Multimedia section
     * @title_de Konfiguration der Multimedia-Bereich (Stories)
     * @description_de Beschriftungen für die Multimedia-Bereich
     */
    multimediaSectionStory: MultimediaImageSectionConfiguration
    /**
     * @title Multimedia files limit
     * @description Maximum number of files one can upload in the Multimedia tab
     * @title_de Beschränkung der Multimediadateien
     * @description_de Maximale Anzahl an Dateien, die auf der Registerkarte „Multimedia“ hochgeladen werden können
     */
    multimediaLimit: number
    /**
     * @title Visibility label
     * @description Label for the checkbox for visibility in the company profile
     * @title_de Label für Sichtbarkeit
     * @description_de Beschriftung für das Kontrollkästchen zur Sichtbarkeit im Firmenprofil
     */
    lblSuppressOrganizationLinkage: LocalizedString
}

export interface EventDatesPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add event date Button
     * @description Label for the Button Add event date
     * @title_de Schaltfläche "Ereignisdatum hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Ereignisdatum hinzufügen
     */
    btnAddEventDate: LocalizedString
    /**
     * @title Add event date Section config
     * @description Label for the Add event date profile page entry
     * @title_de Ereignisdatum hinzufügen Abschnittskonfiguration
     * @description_de Label für den Profilseiteneintrag Ereignisdatum hinzufügen
     */
    sctEventDates: EventDatesSectionConfiguration
}

export interface NonCuratedEventDatesPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add event Button
     * @description Label for the Button Add event
     * @title_de Schaltfläche "Event hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Event hinzufügen
     */
    btnAddEventDate: LocalizedString
    /**
     * @title Add speaker Button
     * @description Label for the Button Add speaker
     * @title_de Schaltfläche "Speaker hinzufügen"
     * @description_de Beschriftung für die Schaltfläche Speaker hinzufügen
     */
    btnAddSpeaker: LocalizedString
    /**
     * @title Speakers modal title
     * @description Title of the modal with selection of speakers
     * @title_de Titel des Speakers-Modals
     * @description_de Titel des Modals mit Speakerauswahl
     */
    lblSpeakersModalTitle: LocalizedString
    /**
     * @title Speakers modal - Speakers list title
     * @description Title of the list of speakers in the modal with selection of speakers
     * @title_de Speakers-Modal - Titel der Speakerliste
     * @description_de Titel der Sprecherliste im Modal mit Speakerauswahl
     */
    lblSpeakersModalSpeakersListTitle: LocalizedString
    /**
     * @title Speakers modal - Roles list title
     * @description Title of the list of roles in the modal with selection of speakers
     * @title_de Speakers-Modal - Titel der Rollenliste
     * @description_de Titel der Rollenliste im Modal mit Speakerauswahl
     */
    lblSpeakersModalFunctionsListTitle: LocalizedString
    /**
     * @title Speakers modal - Confirm button
     * @description Text of the confirmation button in the modal with selection of speakers
     * @title_de Speakers-Modal - Bestätigungsbutton
     * @description_de Text des Bestätigungsbuttons im Modal mit Speakerauswahl
     */
    lblSpeakersModalConfirm: LocalizedString
    /**
     * @title Speakers modal - Cancel button
     * @description Text of the cancellation button in the modal with selection of speakers
     * @title_de Speakers-Modal - Abbruchbutton
     * @description_de Text des Abbruchbuttons im Modal mit Speakerauswahl
     */
    lblSpeakersModalCancel: LocalizedString
    /**
     * @title Event section configuration
     * @description Configuration of the individual event's section
     * @title_de Konfiguration des Eventabschnitts
     * @description_de Konfiguration des Bereichs für einzelne Events
     */
    sctEventDates: EventDatesSectionConfiguration
}

export interface EventDatesSectionConfiguration {
    /**
     * @title Empty Event date title
     * @description Label for the empty Event date title
     * @title_de Empty Event date title
     * @description_de Label for the empty Event date title
     */
    titleEmptyEventDate: LocalizedString
    /**
     * @title Event date image label
     * @description Label for the Event Date image
     * @title_de Bildlabel für Ereignisdatum
     * @description_de Label für das Bild des Veranstaltungsdatums
     */
    lblEventDateImage: LocalizedString
    /**
     * @title Event date image message label
     * @description Label for the Event date image message label input
     * @title_de Etikett der Bildnachricht des Ereignisdatums
     * @description_de Label for the Event date image message label input
     */
    lblEventDateImageMessage: LocalizedString
    /**
     * @title News Image Title label
     * @description Label for the News Image Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Event date title label
     * @description Label for the Event date title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblEventdateTitle: LocalizedString
    /**
     * @title  Event date title placeholder
     * @description Placeholder for the  Event date title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntEventdateTitle: LocalizedString
    /**
     * @title Event date date label
     * @description Label for the Event date date label input
     * @title_de  Datumsetikett des Veranstaltungsdatums
     * @description_de Beschriftung für die Beschriftungseingabe für das Datum des Ereignisdatums
     */
    lblDate: LocalizedString
    /**
     * @title  Event date date placeholder
     * @description Placeholder for the  Event date date input
     * @title_de Platzhalter für das Datum des Ereignisses
     * @description_de Platzhalter für die Datumseingabe des Ereignisdatums
     */
    hntDate: LocalizedString
    /**
     * @title Start time dropdown name
     * @description Name of the list of choosable options for the start time of the event
     * @title_de Name der Dropdownliste „Startzeit“
     * @description_de Name der Liste der wählbaren Optionen für die Startzeit des Events
     */
    lblStartTime: LocalizedString
    /**
     * @title  Event date time placeholder
     * @description Placeholder for the  Event date time input
     * @title_de Platzhalter für Datum und Uhrzeit des Ereignisses
     * @description_de Platzhalter für die Eingabe von Ereignisdatum und Uhrzeit
     */
    hntStartTime: LocalizedString
    /**
     * @title End time dropdown name
     * @description Name of the list of choosable options for the end time of the event
     * @title_de Name der Dropdownliste Endzeit
     * @description_de Name der Liste der wählbaren Optionen für die Endzeit des Events
     */
    lblEndTime: LocalizedString
    /**
     * @title  Event date time placeholder
     * @description Placeholder for the  Event date time input
     * @title_de Platzhalter für Datum und Uhrzeit des Ereignisses
     * @description_de Platzhalter für die Eingabe von Ereignisdatum und Uhrzeit
     */
    hntEndTime: LocalizedString
    /**
     * @title Event date Location label
     * @description Label for the Event date Location label input
     * @title_de  Veranstaltungsdatum Standortlabel
     * @description_de Label für das Event-Datum Ort-Label-Eingabe
     */
    lblLocation: LocalizedString
    /**
     * @title  Event date Location placeholder
     * @description Placeholder for the  Event date Location input
     * @title_de Veranstaltungsdatum Ort Platzhalter
     * @description_de Platzhalter für die Eingabe des Veranstaltungsdatums Ort
     */
    hntLocation: LocalizedString
    /**
     * @title Event type label
     * @description Label for the Event type label input
     * @title_de  Label des Ereignistyps
     * @description_de Label für die Eingabe des Ereignistyp-Labels
     */
    lblEventType: LocalizedString
    /**
     * @title  Event type placeholder
     * @description Placeholder for the  Event type input
     * @title_de Platzhalter für Ereignistyp
     * @description_de Platzhalter für die Eingabe des Ereignistyps
     */
    hntEventType: LocalizedString
    /**
     * @title Part of event series label
     * @description Label for thePart of event series label input
     * @title_de  Teil des Labels der Veranstaltungsreihe
     * @description_de Beschriftung für die Beschriftungseingabe "Teil der Ereignisreihe"
     */
    lblPartEventSeries: LocalizedString
    /**
     * @title  Part of event series placeholder
     * @description Placeholder for the Part of event series input
     * @title_de Teil des Platzhalters für Veranstaltungsreihen
     * @description_de Platzhalter für die Eingabe Teil der Veranstaltungsreihe
     */
    hntPartEventSeries: LocalizedString
    /**
     * @title Listen on label
     * @description Label for the Listen on label input
     * @title_de Auf Etikett anhören
     * @description_de Label für die Listen-on-Label-Eingabe
     */
    lblListenon: LocalizedString
    /**
     * @title Listen on label
     * @description Label for the Listen on label input
     * @title_de Auf Etikett anhören
     * @description_de Label für die Listen-on-Label-Eingabe
     */
    lblListenonTRUE: LocalizedString
    /**
     * @title Listen on label
     * @description Label for the Listen on label input
     * @title_de Auf Etikett anhören
     * @description_de Label für die Listen-on-Label-Eingabe
     */
    lblListenonFALSE: LocalizedString
    /**
     * @title  Listen on placeholder
     * @description Placeholder for the  Listen on input
     * @title_de Platzhalter anhören
     * @description_de Platzhalter für das Listen on input
     */
    hntListenon: LocalizedString
    /**
     * @title Breakout Room
     * @description Label for the Breakout Room  label input
     * @title_de Breakout-Raum
     * @description_de Beschriftung für die Breakout-Raum Beschreibung
     */
    lblBreakoutRoom: LocalizedString
    /**
     * @title Description  label
     * @description Label for the Description  label input
     * @title_de Beschreibungsetikett
     * @description_de Beschriftung für die Beschriftungseingabe Beschreibung
     */
    lblDescription: LocalizedString
    /**
     * @title Description placeholder
     * @description Placeholder for the Description input
     * @title_de Beschreibung Platzhalter
     * @description_de Platzhalter für die Eingabe Beschreibung
     */
    hntDescription: LocalizedString
    /**
     * @title Subtitle label
     * @description Label for the Subtitle label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblSubtitle: LocalizedString
    /**
     * @title Subtitle placeholder
     * @description Placeholder Subtitle input
     * @title_de Untertitel-Platzhalter
     * @description_de Platzhalter-Untertiteleingabe
     */
    hntSubtitle: LocalizedString
    /**
     * @title Speakers label
     * @description Label for the Speakers input
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    lblTabSpeakers: LocalizedString
    /**
     * @title Multimedia label
     * @description Label for the Multimedia input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblTabMultimedia: LocalizedString
    /**
     * @title Keywords label
     * @description Label for the Keywords input
     * @title_de Bezeichnung für Schlüsselwörter
     * @description_de Bezeichnung für die Eingabe Schlüsselwörter
     */
    lblTabKeywords: LocalizedString
    /**
     * @title Prerecorded label
     * @description Label for the Prerecorded input
     * @title_de Aufgezeichnetes Etikett
     * @description_de Label für die Aufnahme mit aufgezeichneter Aufnahme
     */
    lblTabPrerecorded: LocalizedString
    /**
     * @title Prerecorded Tab Description
     * @description Label for Prerecorded Tab Description
     * @title_de Prerecorded Tab Description
     * @description_de Label for Prerecorded Tab Description
     */
    lblPrerecordedDescription: LocalizedString
    /**
     * @title Speakers Tab Description
     * @description Label for Speakers Tab Description
     * @title_de Speakers Tab Description
     * @description_de Label for Speakers Tab Description
     */
    lblSpeakersDescription: LocalizedString
    /**
     * @title Multimedia Tab Description
     * @description Label for Multimedia Tab Description
     * @title_de Multimedia Tab Description
     * @description_de Label for Multimedia Tab Description
     */
    lblMultimediaDescription: LocalizedString
    /**
     * @title Multimedia section configuration
     * @description Labels for the Multimedia section
     * @title_de Konfiguration der Multimedia-Bereich
     * @description_de Beschriftungen für die Multimedia-Bereich
     */
    multimediaSection: MultimediaImageSectionConfiguration
    /**
     * @title Prerecorded video title label
     * @description Placeholder for the Prerecorded video Title input
     * @title_de Prerecorded video title label
     * @description_de Placeholder for the Prerecorded video Title input
     */
    hntDropzoneTitlePrerecorded: LocalizedString
    /**
     * @title Prerecorded video Properties placeholder
     * @description Placeholder for the Prerecorded video Properties input
     * @title_de Prerecorded video Properties placeholder
     * @description_de Placeholder for the Prerecorded video Properties input
     */
    hntDropzonePropPrerecorded: LocalizedString
    /**
     * @title Multimedia files limit
     * @description Maximum number of files one can upload in the Multimedia tab
     * @title_de --
     * @description_de --
     */
    multimediaLimit: number
    /**
     * @title Person functions Whitelist
     * @description List of person function ids for contact persons
     * @title_de Kategorie Whitelist
     * @description_de Liste der Personenfunktions-IDs für für Ansprechpartner
     */
    personFunctionWhitelist: string[]
}

export interface ShowroomsPageConfiguration {
    /**
     * @title Page Title public shorooms
     * @description Label for Page Title public shorooms
     * @title_de Seitentitel
     * @description_de Beschriftung für Seitentitel
     */
    pageTitlePublic: LocalizedString
    /**
     * @title Page Description public shorooms
     * @description Label for Page Description public shorooms
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescriptionPublic: LocalizedString
    /**
     * @title Page Title private shorooms
     * @description Label for Page Title private shorooms
     * @title_de Seitentitel
     * @description_de Beschriftung für Seitentitel
     */
    pageTitlePrivate: LocalizedString
    /**
     * @title Page Description private shorooms
     * @description Label for Page Description private shorooms
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescriptionPrivate: LocalizedString
    /**
     * @title Add showrooms Button
     * @description Label for the Button Add showrooms
     * @title_de --
     * @description_de --
     */
    btnAddShowrooms: LocalizedString
    /**
     * @title Add event date Section config
     * @description Label for the Add event date profile page entry
     * @title_de Ereignisdatum hinzufügen Abschnittskonfiguration
     * @description_de Label für den Profilseiteneintrag Ereignisdatum hinzufügen
     */
    sctShowrooms: ShowroomsSectionConfiguration
}

export interface ShowroomsSectionConfiguration {
    /**
     * @title Empty Showrooms title
     * @description Label for the empty Showrooms title
     * @title_de --
     * @description_de --
     */
    titleEmptyShowrooms: LocalizedString
    /**
     * @title Showrooms image label
     * @description Label for the Showrooms image
     * @title_de Bildlabel für Ereignisdatum
     * @description_de Label für das Bild des Veranstaltungsdatums
     */
    lblShowroomsImage: LocalizedString
    /**
     * @title Showrooms image message label
     * @description Label for the Showrooms image message label input
     * @title_de Etikett der Bildnachricht des Ereignisdatums
     * @description_de Label for the Event date image message label input
     */
    lblShowroomsImageMessage: LocalizedString
    /**
     * @title Showrooms Title label
     * @description Label for the Showrooms Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Showrooms title label
     * @description Label for the Showrooms title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblShowroomstitle: LocalizedString
    /**
     * @title  Showrooms title placeholder
     * @description Placeholder for the  Showrooms title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntShowroomstitle: LocalizedString
    /**
     * @title Description  label
     * @description Label for the Description  label input
     * @title_de Beschreibungsetikett
     * @description_de Beschriftung für die Beschriftungseingabe Beschreibung
     */
    lblDescription: LocalizedString
    /**
     * @title Description placeholder
     * @description Placeholder for the Description input
     * @title_de Beschreibung Platzhalter
     * @description_de Platzhalter für die Eingabe Beschreibung
     */
    hntDescription: LocalizedString
    /**
     * @title Access Method label
     * @description Label for the Access Method label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblAccessMethod: LocalizedString
    /**
     * @title Access Method placeholder
     * @description Placeholder for the Access Method input
     * @title_de Beschreibung Platzhalter
     * @description_de Platzhalter für die Eingabe Beschreibung
     */
    hntAccessMethod: LocalizedString
    /**
     * @title Option Access Method1 label
     * @description Label for the Option Access Method input
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    lblOptionAccessMethod1: LocalizedString
    /**
     * @title Option Access Method2 label
     * @description Label for the Option Access Method2 input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblOptionAccessMethod2: LocalizedString
    /**
     * @title Mock showroom title (English)
     * @description Title for mock showroom item in English
     * @title_de Schein-Showroom-Titel
     * @description_de Titel für Schein-Showroom-Artikel in Englisch
     */
    titleMockShowroomEn: string
    /**
     * @title Mock showroom title (German)
     * @description Title for mock showroom item (German)
     * @title_de Schein-Showroom-Titel (Deutsch)
     * @description_de Titel für Schein-Showroom-Artikel in Deutsch
     */
    titleMockShowroomDe: string
}

export interface CouponsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add Coupons Button
     * @description Label for the Button Add Coupons
     * @title_de --
     * @description_de --
     */
    btnAddCoupons: LocalizedString
    /**
     * @title Coupons edit section config
     * @description Config for the section where the Coupon's fields are edited
     * @title_de Coupons edit section config
     * @description_de Config for the section where the Coupon's fields are edited
     */
    sctCoupons: CouponsSectionConfiguration
}

export interface CouponsSectionConfiguration {
    /**
     * @title Empty Coupons name
     * @description Label for the empty Coupons name
     * @title_de --
     * @description_de --
     */
    nameEmptyCoupons: LocalizedString
    /**
     * @title Coupons image label
     * @description Label for the Coupons image
     * @title_de Coupons image label
     * @description_de Label for the Coupons image
     */
    lblCouponsImage: LocalizedString
    /**
     * @title Coupons image message label
     * @description Label for the Coupons image message label input
     * @title_de Coupons image message label
     * @description_de Label for the Coupons image message label input
     */
    lblCouponsImageMessage: LocalizedString
    /**
     * @title Coupons image placeholder Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntPlaceholderTitle: LocalizedString
    /**
     * @title Coupons image placeholder Properties label
     * @description Label for the Company image placeholder Properties
     * @title_de Coupons image placeholder Properties label
     * @description_de Label for the Company image placeholder Properties
     */
    hntPlaceholderProp: LocalizedString
    /**
     * @title Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Coupons name label
     * @description Label for the Coupons name label input
     * @title_de Coupons name label
     * @description_de Label for the Coupons name label input
     */
    lblCouponsName: LocalizedString
    /**
     * @title Coupons name placeholder
     * @description Placeholder for the Coupons name input
     * @title_de Coupons name placeholder
     * @description_de Placeholder for the Coupons name input
     */
    hntCouponsName: LocalizedString
    /**
     * @title URL label
     * @description Label for the URL label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblURL: LocalizedString
    /**
     * @title URL placeholder
     * @description Label for the URL placeholder input
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    hntURL: LocalizedString
    /**
     * @title Start date label
     * @description Label for the Start date input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblCouponsValidStart: LocalizedString
    /**
     * @title Start date placeholder
     * @description Placeholder for the Start date
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    hntCouponsValidStart: LocalizedString
    /**
     * @title End date label
     * @description Label for the End date input
     * @title_de Multimedia-Label
     * @description_de Beschriftung für den Multimedia-Eingang
     */
    lblCouponsValidEnd: LocalizedString
    /**
     * @title End date plaecholder
     * @description Placeholder for the End date
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    hntCouponsValidEnd: LocalizedString
    /**
     * @title Description label
     * @description Label for the Description label input
     * @title_de Beschreibungsetikett
     * @description_de Beschriftung für die Beschriftungseingabe Beschreibung
     */
    lblDescriptionTitle: LocalizedString
    /**
     * @title Description title placeholder
     * @description Placeholder for the Description title input
     * @title_de Description title placeholder
     * @description_de Placeholder for the Description title input
     */
    hntDescriptionTitle: LocalizedString
    /**
     * @title Description long label
     * @description Label for the Description long input
     * @title_de Description long label
     * @description_de Label for the Description long input
     */
    lblDescriptionLong: LocalizedString
    /**
     * @title Description long placeholder
     * @description Placeholder for the Description long input
     * @title_de Description long placeholder
     * @description_de Placeholder for the Description long input
     */
    hntDescriptionLong: LocalizedString
    /**
     * @title Description short label
     * @description Label for the Description short label input
     * @title_de Description long label
     * @description_de Label for the Description short label input
     */
    lblDescriptionShort: LocalizedString
    /**
     * @title Description short placeholder
     * @description Placeholder for the Description short input
     * @title_de Description short placeholder
     * @description_de Placeholder for the Description short input
     */
    hntDescriptionShort: LocalizedString
    /**
     * @title Coupons start date
     * @description Coupons valid start date
     * @title_de Startdatum der Coupons
     * @description_de Gültiges Startdatum des Coupons
     */
    dateCouponsValidStart: String
    /**
     * @title Coupons end date
     * @description Coupons valid end date
     * @title_de Enddatum der Coupons
     * @description_de Gültigkeitsende der Coupons
     */
    dateCouponsValidEnd: String
}

export interface JobsPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add jobs Button
     * @description Label for the Button Add jobs
     * @title_de --
     * @description_de --
     */
    btnAddJobs: LocalizedString
    /**
     * @title Jobs Section config
     * @description Label for the Jobs section profile page entry
     * @title_de Ereignisdatum hinzufügen Abschnittskonfiguration
     * @description_de Label für den Profilseiteneintrag Ereignisdatum hinzufügen
     */
    sctJobs: JobsSectionConfiguration
}

export interface JobsSectionConfiguration {
    /**
     * @title Label Jobs title
     * @description Label for the  Jobs
     * @title_de --
     * @description_de --
     */
    labelJobs: LocalizedString
    /**
     * @title Empty Jobs title
     * @description Label for the empty Jobs title
     * @title_de --
     * @description_de --
     */
    titleEmptyJobs: LocalizedString
    /**
     * @title Jobs Title label
     * @description Label for the Jobs Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntTitleJob: LocalizedString
    /**
     * @title Job links label
     * @description Label for the job links input
     * @title_de  Bezeichnung für Job-Links
     * @description_de Bezeichnung für die Eingabe von Job-Links
     */
    lblTabLinks: LocalizedString
    /**
     * @title Job Links Tab Description
     * @description Label for Job Links Tab Description
     * @title_de Beschreibung des Job-Links-Tab
     * @description_de Bezeichnung für die Beschreibung des Job-Links-Tab
     */
    lblLinksDescription: LocalizedString
    /**
     * @title Jobs image label
     * @description Label for the Jobs image
     * @title_de --
     * @description_de --
     */
    lblJobsImage: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblJobsImageMessage: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
    /**
     * @title Start displaying on title label
     * @description Label for the Start displaying on title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblStartDisplayinOnDate: LocalizedString
    /**
     * @title  Start displaying on title placeholder
     * @description Placeholder for the  Start displaying on title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntStartDisplayinOnDate: LocalizedString
    /**
     * @title End displaying on title label
     * @description Label for theEnd displaying on title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblEndDisplayinOnDate: LocalizedString
    /**
     * @title  End displaying on title placeholder
     * @description Placeholder for the  End displaying on title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntEndDisplayinOnDate: LocalizedString
    /**
     * @title Job start title label
     * @description Label for the Job start title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblJobStartDate: LocalizedString
    /**
     * @title  Job start title placeholder
     * @description Placeholder for the Job start title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntJobStartDate: LocalizedString
    /**
     * @title Job location title label
     * @description Label for the Job location title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblJobLocation: LocalizedString
    /**
     * @title  Job location title placeholder
     * @description Placeholder for the Job location title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntJobLocation: LocalizedString
    /**
     * @title Employment type title label
     * @description Label for the Employment type title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblEmploymentType: LocalizedString
    /**
     * @title  Employment type title placeholder
     * @description Placeholder for the Employment type title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntEmploymentType: LocalizedString
    /**
     * @title Employment Type
     * @description Type of employment available
     * @title_de Art der Beschäftigung
     * @description_de Art der verfügbaren Beschäftigung
     */
    employmentType: ValidationFieldNames[]
    /**
     * @title Employment level title label
     * @description Label for the Employment level title label input
     * @title_de Titelbezeichnung des Veranstaltungsdatums
     * @description_de Label für die Eingabe des Titellabels für das Ereignisdatum
     */
    lblEmploymentLevel: LocalizedString
    /**
     * @title  Employment level title placeholder
     * @description Placeholder for the Employment level title input
     * @title_de Platzhalter für den Titel des Veranstaltungsdatums
     * @description_de Platzhalter für die Eingabe des Ereignisdatumstitels
     */
    hntEmploymentLevel: LocalizedString
    /**
     * @title Career Level
     * @description Career Level available
     * @title_de Karrierestufe
     * @description_de Verfügbare Karrierestufen
     */
    careerLevel: ValidationFieldNames[]
    /**
     * @title Salary group title label
     * @description Label for the Salary group title label input
     * @title_de Titelbezeichnung der Gehaltsgruppe
     * @description_de Beschriftung für die Eingabe des Titels der Gehaltsgruppe
     */
    lblSalaryGroup: LocalizedString
    /**
     * @title Salary group title placeholder
     * @description Placeholder for the Salary group title input
     * @title_de Platzhalter für Gruppentitel
     * @description_de Platzhalter für die Eingabe des Titels der Gehaltsgruppe
     */
    hntSalaryGroup: LocalizedString
    /**
     * @title Salary Group
     * @description Salary Group available
     * @title_de Gehaltsgruppe
     * @description_de Gehaltsgruppe verfügbar
     */
    salaryGroup: ValidationFieldNames[]
    /**
     * @title Description  label
     * @description Label for the Description  label input
     * @title_de Beschreibungsetikett
     * @description_de Beschriftung für die Beschriftungseingabe Beschreibung
     */
    lblJobDescription: LocalizedString
    /**
     * @title Description placeholder
     * @description Placeholder for the Description input
     * @title_de Beschreibung Platzhalter
     * @description_de Platzhalter für die Eingabe Beschreibung
     */
    hntJobDescription: LocalizedString
    /**
     * @title Job requirements label
     * @description Label for the Job requirements label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblJobReqirements: LocalizedString
    /**
     * @title Job requirements placeholder
     * @description Label for the  Job requirements placeholder input
     * @title_de Lautsprecher Label
     * @description_de Bezeichnung für die Eingabe Lautsprecher
     */
    hntJobReqirements: LocalizedString
    /**
     * @title Categories label
     * @description Label for the Categories input
     * @title_de Kategorien Label
     * @description_de Bezeichnung für die Eingabe Kategorien
     */
    lblTabCategories: LocalizedString
    /**
     * @title Tab Contacts label
     * @description Label for the Tab Contacts label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblTabContacts: LocalizedString
    /**
     * @title Tab Multimedia label
     * @description Label for the Tab Multimedia label input
     * @title_de Untertitel-Label
     * @description_de Label für die Untertitel-Label-Eingabe
     */
    lblTabMultimedia: LocalizedString
    /**
     * @title Categories Tab Description
     * @description Label for Categories Tab Description
     * @title_de Categories Tab Description
     * @description_de Label for Categories Tab Description
     */
    lblCategoriesDescription: LocalizedString
    /**
     * @title Contacts Tab Description
     * @description Label for Contacts Tab Description
     * @title_de Contacts Tab Description
     * @description_de Label for Contacts Tab Description
     */
    lblContactsDescription: LocalizedString
    /**
     * @title Multimedia Tab Description
     * @description Label for Multimedia Tab Description
     * @title_de Multimedia Tab Description
     * @description_de Label for Multimedia Tab Description
     */
    lblMultimediaDescription: LocalizedString
    /**
     * @title Multimedia section configuration
     * @description Labels for the Multimedia section
     * @title_de Konfiguration der Multimedia-Bereich
     * @description_de Beschriftungen für die Multimedia-Bereich
     */
    multimediaSection: MultimediaImageSectionConfiguration
    /**
     * @title Homeoffice label
     * @description Label for Homeoffice radio button group
     * @title_de --
     * @description_de --
     */
    lblHomeoffice: LocalizedString
    /**
     * @title Homeoffice label NA
     * @description Label for Homeoffice radio button - option NA
     * @title_de --
     * @description_de --
     */
    lblHomeofficeNA: LocalizedString
    /**
     * @title Homeoffice label NO
     * @description Label for Homeoffice radio button - option NO
     * @title_de --
     * @description_de --
     */
    lblHomeofficeNO: LocalizedString
    /**
     * @title Homeoffice label YES
     * @description Label for Homeoffice radio button - option YES
     * @title_de --
     * @description_de --
     */
    lblHomeofficeYES: LocalizedString
    /**
     * @title Multimedia files limit
     * @description Maximum number of files one can upload in the Multimedia tab
     * @title_de --
     * @description_de --
     */
    multimediaLimit: number
    /**
     * @title Person functions Whitelist
     * @description List of person function ids for contact persons
     * @title_de Kategorie Whitelist
     * @description_de Liste der Personenfunktions-IDs für für Ansprechpartner
     */
    personFunctionWhitelist: string[]
    /**
     * @title Category Whitelist
     * @description List of category ids for root elements
     * @title_de Kategorie Whitelist
     * @description_de Liste von Kategorie IDs für Rootelemenete
     */
    categoryWhitelist: string[]
    /**
     * @title Job background image label
     * @description Label for Job background image
     * @title_de --
     * @description_de --
     */
    lblJobBackgroundImage: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteJobBackgroundImg: LocalizedString
    /**
     * @title Jobs background image message label
     * @description Label for the Jobs background image message label input
     * @title_de --
     * @description_de --
     */
    lblJobsBackgroundImgMessage: LocalizedString
}

export interface NotificationTemplatesPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Add notifications button
     * @description Label for the Button Add notifications
     * @title_de Schaltfläche zum Hinzufügen von Benachrichtigungen
     * @description_de Label für das Button zum Hinzufügen von Benachrichtigungen
     */
    btnAddNotificationTemplates: LocalizedString
    /**
     * @title Notification templates Section config
     * @description Label for the Notification templates section profile page entry
     * @title_de „Notification-Templates“ Abschnittskonfiguration
     * @description_de Label für den Profilseiteneintrag „Notification-Templates“
     */
    sctNotificationTemplates: NotificationsSectionConfiguration
}

export interface NotificationsSectionConfiguration {
    /**
     * @title Empty notification template title
     * @description Label for the empty notification template title
     * @title_de Empty notification template title
     * @description_de Label for the empty notification template title
     */
    titleEmptyNotificationTemplate: LocalizedString
    /**
     * @title Date label
     * @description Label for the date input
     * @title_de Datum-Label
     * @description_de Label für die Datumseingabe
     */
    lblDate: LocalizedString
    /**
     * @title Date placeholder
     * @description Placeholder for the date input
     * @title_de Datumsplatzhalter
     * @description_de Platzhalter für die Datumseingabe
     */
    hntDate: LocalizedString
    /**
     * @title Time label
     * @description Label for the time input
     * @title_de Zeit-Label
     * @description_de Label für die Zeiteingabe
     */
    lblTime: LocalizedString
    /**
     * @title Time placeholder
     * @description Placeholder for the time input
     * @title_de Zeitplatzhalter
     * @description_de Platzhalter für die Zeiteingabe
     */
    hntTime: LocalizedString
    /**
     * @title Time info message
     * @description Info message for the time input
     * @title_de Zeitinfomeldung
     * @description_de Infomeldung zur Zeiteingabe
     */
    infoTime: LocalizedString
    /**
     * @title Destination label
     * @description Label for the destination input
     * @title_de Bestimmungsort-Label
     * @description_de Label  für die Bestimmungsorteingabe
     */
    lblDestination: LocalizedString
    /**
     * @title Destination placeholder
     * @description Placeholder for the destination input
     * @title_de Bestimmungsort-Platzhalter
     * @description_de Platzhalter für die Bestimmungsorteingabe
     */
    hntDestination: LocalizedString
    /**
     * @title Text label
     * @description Label for the the text input
     * @title_de Text-Label
     * @description_de Label für die Textneingabe
     */
    lblText: LocalizedString
    /**
     * @title Text placeholder
     * @description Placeholder for the text input
     * @title_de Text-Platzhalter
     * @description_de Platzhalter für die Texteingabe
     */
    hntText: LocalizedString
    /**
     * @title Text template - English
     * @description Template for the text input
     * @title_de Vorlage für Text - Englisch
     * @description_de Vorlage für die Texteingabe
     */
    tmpText: string
    /**
     * @title Text template - German
     * @description Template for the text input
     * @title_de Vorlage für Text - Deutsch
     * @description_de Vorlage für die Texteingabe
     */
    tmpTextDe: string
    /**
     * @title Maximum send-out amount label
     * @description Label for the the maximum send-out amount input
     * @title_de Maximaler-Versandbetrag-Label
     * @description_de Label für die Eingabe des maximalen Versandbetrags
     */
    lblMaxUsageCount: LocalizedString
    /**
     * @title Maximum send-out amount placeholder
     * @description Placeholder for the text input
     * @title_de Maximaler-Versandbetrag-Platzhalter
     * @description_de Platzhalter für die Eingabe des maximalen Versandbetrags
     */
    hntMaxUsageCount: LocalizedString
    /**
     * @title Criteria list title
     * @description Title for the criteria list
     * @title_de Titel der Kriterienliste
     * @description_de Titel der Kriterienliste
     */
    lblCriteriaList: LocalizedString
    /**
     * @title Criteria list description
     * @description Description for the for the criteria list
     * @title_de Beschreibung der Kriterienliste
     * @description_de Beschreibung der Kriterienliste
     */
    hntCriteriaList: LocalizedString
    /**
     * @title Criteria list counter
     * @description Counter of selected criteria in the list
     * @title_de Kriterienlistenzähler
     * @description_de Zähler der ausgewählten Kriterien in der Liste
     */
    msgCriteriaList: LocalizedString
    /**
     * @title Package modal title
     * @description Title of the modal with selection of subelement packages
     * @title_de Titel des Paketmodals
     * @description_de Titel des Modals mit Auswahl der Unterelementpakete
     */
    lblPackageModalTitle: LocalizedString
    /**
     * @title Package modal description
     * @description Title of the modal with selection of subelement packages
     * @title_de Beschreibung des Paketmodals
     * @description_de Beschreibung des Modals mit Auswahl der Unterelementpakete
     */
    lblPackageModalDescription: LocalizedString
    /**
     * @title Package modal - Confirm button
     * @description Text of the confirmation button in the modal with selection of subelement packages
     * @title_de Paketmodal - Bestätigungsbutton
     * @description_de Text des Bestätigungsbuttons im Modal mit Auswahl von Unterelementpaketen
     */
    lblPackageModalConfirm: LocalizedString
    /**
     * @title Package modal - Cancel button
     * @description Text of the cancellation button in the modal with selection of subelement packages
     * @title_de Paketmodal - Abbruchbutton
     * @description_de Text des Abbruchbuttons im Modal mit Auswahl der Unterelementpakete
     */
    lblPackageModalCancel: LocalizedString
    /**
     * @title Criteria list counter
     * @description Counter of selected criteria in the list
     * @title_de Kriterienlistenzähler
     * @description_de Zähler der ausgewählten Kriterien in der Liste
     */
    lblBlockTriggerModalTitle: LocalizedString
    /**
     * @title Criteria list counter
     * @description Counter of selected criteria in the list
     * @title_de Kriterienlistenzähler
     * @description_de Zähler der ausgewählten Kriterien in der Liste
     */
    lblBlockTriggerModalDescription: LocalizedString
    /**
     * @title Show dates & times section
     * @description Toggle visibility of dates & times section
     * @title_de Abschnitt „Datum und Uhrzeit“ anzeigen
     * @description_de Schalten Sie die Sichtbarkeit des Abschnitts „Datum und Uhrzeit“ um
     */
    showDateTimeSection: boolean
    /**
     * @title Dates list
     * @description List of dates when the notification is used
     * @title_de Datumsliste
     * @description_de Liste der Daten, an denen die Benachrichtigung verwendet wird
     */
    dates: NotificationTemplateDate[]
    /**
     * @title Show time picker
     * @description Toggle visibility of the time picker
     * @title_de Zeitauswahl anzeigen
     * @description_de Schalten Sie die Sichtbarkeit der Zeitauswahl um
     */
    showTimePicker: boolean
    /**
     * @title Hours list
     * @description List of hours in the day when the notification is used (use 00:00 - 23:59 to mark the entire day)
     * @title_de Stundenliste
     * @description_de Liste der Stunden am Tag, zu denen die Benachrichtigung verwendet wird (Verwenden Sie 00:00 - 23:59, um den gesamten Tag zu markieren)
     */
    times: NotificationTemplateTime[]
    /**
     * @title Entire day label
     * @description Label shown in hours list when set time is 00:00 - 23:59
     * @title_de Label für den ganzen Tag
     * @description_de Die Beschriftung wird in der Stundenliste angezeigt, wenn die eingestellte Zeit 00:00 - 23:59 ist
     */
    lblEntireDay: LocalizedString
    /**
     * @title Default start date
     * @description Default start date for notification template if none is chosen
     * @title_de Standardstartdatum
     * @description_de Standardstartdatum für die Benachrichtigung, wenn keines ausgewählt ist
     */
    defaultStartDate: string
    /**
     * @title Default end date
     * @description Default end date for notification template if none is chosen
     * @title_de Standard-Enddatum
     * @description_de Standard-Enddatum für die Benachrichtigung, wenn keines ausgewählt ist
     */
    defaultEndDate: string
    /**
     * @title Empty coupon title
     * @description Label for the empty coupon title
     * @title_de Empty coupon title
     * @description_de Label for the empty coupon title
     */
    titleEmptyCoupon: LocalizedString
    /**
     * @title Coupons name label
     * @description Label for the Coupons name label input
     * @title_de Coupons name label
     * @description_de Label for the Coupons name label input
     */
    lblCouponsName: LocalizedString
    /**
     * @title Coupons name placeholder
     * @description Placeholder for the Coupons name input
     * @title_de Coupons name placeholder
     * @description_de Placeholder for the Coupons name input
     */
    hntCouponsName: LocalizedString
    /**
     * @title Description label
     * @description Label for the Description label input
     * @title_de Beschreibungsetikett
     * @description_de Beschriftung für die Beschriftungseingabe Beschreibung
     */
    lblDescriptionTitle: LocalizedString
    /**
     * @title Description title placeholder
     * @description Placeholder for the Description title input
     * @title_de Description title placeholder
     * @description_de Placeholder for the Description title input
     */
    hntDescriptionTitle: LocalizedString
    /**
     * @title Description long label
     * @description Label for the Description long input
     * @title_de Description long label
     * @description_de Label for the Description long input
     */
    lblDescriptionLong: LocalizedString
    /**
     * @title Description long placeholder
     * @description Placeholder for the Description long input
     * @title_de Description long placeholder
     * @description_de Placeholder for the Description long input
     */
    hntDescriptionLong: LocalizedString
}

export interface NotificationTemplateDate {
    /**
     * @title ID
     * @description ID
     * @title_de ID
     * @description_de ID
     */
    id: string
    /**
     * @title Date (format YYYY-MM-DD)
     * @description Date (format YYYY-MM-DD)
     * @title_de Datum (format YYYY-MM-DD)
     * @description_de Datum (format YYYY-MM-DD)
     */
    date: string
}

export interface NotificationTemplateTime {
    /**
     * @title ID
     * @description ID
     * @title_de ID
     * @description_de ID
     */
    id: string
    /**
     * @title Start (format HH:mm, 24h)
     * @description Start (format HH:mm, 24h)
     * @title_de Start (format HH:mm, 24h)
     * @description_de Start (format HH:mm, 24h)
     */
    start: string
    /**
     * @title End (format HH:mm, 24h)
     * @description End (format HH:mm, 24h)
     * @title_de Ende (format HH:mm, 24h)
     * @description_de Ende (format HH:mm, 24h)
     */
    end: string
}

export enum PrintSection {
    LOGO = "LOGO",
    COMPANY_NAME = "COMPANY_NAME",
    COMPANY_SORT = "COMPANY_SORT",
    ADDRESS = "ADDRESS",
    CONTACT = "CONTACT",
    DESCRIPTION = "DESCRIPTION",
    ADS = "ADS"
}

export interface PrintPageConfiguration {
    /**
     * @title Page Description
     * @description Label for Page Description
     * @title_de Seitenbeschreibung
     * @description_de Beschriftung für Seitenbeschreibung
     */
    pageDescription: LocalizedString
    /**
     * @title Sections order
     * @description Order of sections in the print page, section is not shown in the print catalogue if not listed here
     * @title_de Sektionsordnung
     * @description_de Ordnung der Sektionen auf der Printseite, Sektion wird nicht im Printkatalog angezeigt, wenn sie hier nicht aufgeführt ist
     */
    sectionsOrder: PrintSection[]
    /**
     * @title Deadline
     * @description Sets the deadline when the print will be disabled.
     * @title_de Deadline
     * @description_de Setzt die Deadline ab dem der Print deaktiviert wird.
     */
    deadLine: string
    /**
     * @title PrintDeadline label
     * @description Label for PrintDeadline field
     * @title_de --
     * @description_de --
     */
    printDeadline: LocalizedString
    /**
     * @title Copy data label
     * @description Label for the Copy data toggle
     * @title_de --
     * @description_de --
     */
    lblCopyData: LocalizedString
    /**
     * @title Background logo config
     * @description Config for the logo section
     * @title_de --
     * @description_de --
     */
    sctLogoBackgroundImage: LogoBackgroundImageSectionConfiguration
    /**
     * @title Name Section config
     * @description Configuration for the name section
     * @title_de --
     * @description_de --
     */
    sctCompanyName: CompanyNameSectionConfiguration
    /**
     * @title Sort Section config
     * @description Configuration for the sort section
     * @title_de --
     * @description_de --
     */
    sctCompanySort: CompanySortSectionConfiguration
    /**
     * @title Address Section config
     * @description Configuration for the Address section print page entry
     * @title_de --
     * @description_de --
     */
    sctAddressPrintCatalogue: AddressPrintCatalogueSectionConfiguration
    /**
     * @title Contact Section config
     * @description Configuration for the Contact section
     * @title_de --
     * @description_de --
     */
    sctContactInformation: ContactInformationSectionConfiguration
    /**
     * @title Description Section config
     * @description Configuration for the Description section
     * @title_de --
     * @description_de --
     */
    sctCompanyDescription: CompanyDescriptionSectionConfiguration
    /**
     * @title Ad Section config
     * @description Configuration for the Ad section
     * @title_de --
     * @description_de --
     */
    sctAds: AdsSectionConfiguration
}

export interface LogoBackgroundImageSectionConfiguration {
    /**
     * @title Jobs image label
     * @description Label for the Jobs image
     * @title_de --
     * @description_de --
     */
    lblSectionTitleLogoBackground: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblCompanyLogo: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblCompanyLogoImageMessage: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Label for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntProp: LocalizedString
    /**
     * @title  Browse files Button
     * @description Label for the Button Browse files
     * @title_de Schaltfläche "Dateien durchsuchen"
     * @description_de Bezeichnung für die Schaltfläche Dateien durchsuchen
     */
    btnBrowseFiles: LocalizedString
    /**
     * @title  Delete current image Button
     * @description Label for the Button Delete current image
     * @title_de Aktuelles Bild löschen Button
     * @description_de Beschriftung für die Schaltfläche Aktuelles Bild löschen
     */
    btnDeleteImg: LocalizedString
}

export interface CompanyNameSectionConfiguration {
    /**
     * @title Company name section label
     * @description Label for the Company name section
     * @title_de --
     * @description_de --
     */
    lblSectionCompanyName: LocalizedString
    /**
     * @title Company name label
     * @description Label for the Company name label input
     * @title_de --
     * @description_de --
     */
    lblCompanyName: LocalizedString
    /**
     * @title Company name label
     * @description Label for the Company name input
     * @title_de --
     * @description_de --
     */
    hntCompanyName: LocalizedString
}

export interface CompanySortSectionConfiguration {
    /**
     * @title Company sort section label
     * @description Label for the Company sort section
     * @title_de --
     * @description_de --
     */
    lblSectionCompanySort: LocalizedString
    /**
     * @title Company sort label
     * @description Label for the Compay sort label input
     * @title_de --
     * @description_de --
     */
    lblCompanySort: LocalizedString
}

export interface AddressPrintCatalogueSectionConfiguration {
    /**
     * @title Jobs image label
     * @description Label for the Jobs image
     * @title_de --
     * @description_de --
     */
    lblSectionAddressInformation: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblAddress1: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntAddress1: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblAddress2: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntAddress2: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblAddress3: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntAddress3: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblZipCode: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntZipCode: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblCity: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntCity: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblCountry: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntCountry: LocalizedString
}

export interface ContactInformationSectionConfiguration {
    /**
     * @title Jobs image label
     * @description Label for the Jobs image
     * @title_de --
     * @description_de --
     */
    lblSectionContactInfo: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblPhone: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntPhone: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblMobile: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntMobile: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblFax: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntFax: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblEmail: LocalizedString
    /**
     * @title Coupons Title label
     * @description Label for the Coupons Title input
     * @title_de Titelbezeichnung des Nachrichten
     * @description_de Beschriftung für die Eingabe des Nachrichten
     */
    hntEmail: LocalizedString
    /**
     * @title Jobs image message label
     * @description Label for the Jobs image message label input
     * @title_de --
     * @description_de --
     */
    lblWebsite: LocalizedString
    /**
     * @title Website hint
     * @description Hint for the Website input
     * @title_de --
     * @description_de --
     */
    hntWebsite: LocalizedString
}

export interface CompanyDescriptionSectionConfiguration {
    /**
     * @title Print description label
     * @description Label for the Description section
     * @title_de --
     * @description_de --
     */
    lblSectionCompanyDescription: LocalizedString
    /**
     * @title Company Portrait label
     * @description Label for the Company Portrait text area
     * @title_de --
     * @description_de --
     */
    lblCompanyPortrait: LocalizedString
    /**
     * @title Company Portrait hint
     * @description Hint for the searchbar input field
     * @title_de --
     * @description_de --
     */
    hntCompanyPortrait: LocalizedString
}
export interface AdsSectionConfiguration {
    /**
     * @title Ads Section label
     * @description Label for the Ads Section
     * @title_de --
     * @description_de --
     */
    lblAdsSection: LocalizedString
    /**
     * @title Ads Section description label
     * @description Label for the description of the Ads Section
     * @title_de --
     * @description_de --
     */
    lblAdsDecription: LocalizedString
    /**
     * @title Company Image Title label
     * @description Placeholder for the Company Image Title input
     * @title_de Titelbezeichnung des Firmenbilds
     * @description_de Beschriftung für die Eingabe des Unternehmensbildtitels
     */
    hntDropzoneTitle: LocalizedString
    /**
     * @title Company Image Properties placeholder
     * @description Placeholder for the Company Image Properties input
     * @title_de Eigenschaften des Unternehmensimages
     * @description_de Bezeichnung für die Eingabe "Unternehmensbildeigenschaften"
     */
    hntDropzoneProp: LocalizedString
    /**
     * @title Position label
     * @description Label for the Position label input
     * @title_de Positionsbezeichnung
     * @description_de Label für die Positionslabel-Eingabe
     */
    lblHeaderPosition: LocalizedString
    /**
     * @title Preview label
     * @description Label for the Preview label input
     * @title_de Etikettenvorschau
     * @description_de Label für die Label-Eingabe in der Vorschau
     */
    lblHeaderPreview: LocalizedString
    /**
     * @title File label
     * @description Label for the File label input
     * @title_de Dateibezeichnung
     * @description_de Label für die Dateilabel-Eingabe
     */
    lblHeaderFile: LocalizedString
}

export interface EventShopPageConfiguration {
    /**
     * @title Page Title
     * @description Label for Page Title
     * @title_de --
     * @description_de --
     */
    eventShopUrl: LocalizedString
}

export interface ValidationFieldNames {
    /**
     * @title Validated field's name
     * @description Field name as key for validated fields
     * @title_de --
     * @description_de --
     */
    name: string
    /**
     * @title Validated field's label
     * @description Label to be shown for the validated field
     * @title_de --
     * @description_de --
     */
    value: LocalizedString
}

export interface ValidationSectionConfiguration {
    /**
     * @title Section's label
     * @description Label for the validated section with erroneous fields
     * @title_de --
     * @description_de --
     */
    name: LocalizedString
    /**
     * @title Section's label
     * @description Label for the validated section with erroneous fields
     * @title_de --
     * @description_de --
     */
    nameIndex: LocalizedString
    /**
     * @title Field mames's labels
     * @description Array of labels for validated fields
     * @title_de --
     * @description_de --
     */
    fieldNames: ValidationFieldNames[]
}

export interface ValidationFieldNamesConfiguration {
    /**
     * @title Organization's validated fields' names
     * @description Validation configuration for the organization section
     * @title_de --
     * @description_de --
     */
    organization: ValidationSectionConfiguration
    /**
     * @title Links validated fields' names
     * @description Validation configuration for the links section
     * @title_de --
     * @description_de --
     */
    links: ValidationSectionConfiguration
    /**
     * @title Products validated fields' names
     * @description Validation configuration for the products section
     * @title_de --
     * @description_de --
     */
    products: ValidationSectionConfiguration
    /**
     * @title Products validated fields' names
     * @description Validation configuration for the products section
     * @title_de --
     * @description_de --
     */
    productsForCollections: ValidationSectionConfiguration
    /**
     * @title Trademarks validated fields' names
     * @description Validation configuration for the trademarks section
     * @title_de --
     * @description_de --
     */
    trademarks: ValidationSectionConfiguration
    /**
     * @title Persons validated fields' names
     * @description Validation configuration for the persons section
     * @title_de --
     * @description_de --
     */
    persons: ValidationSectionConfiguration
    /**
     * @title MEdias validated fields' names
     * @description Validation configuration for the medias section
     * @title_de --
     * @description_de --
     */
    medias: ValidationSectionConfiguration
    /**
     * @title Exhibitor news validated fields' names
     * @description Validation configuration for the Exhibitor news section
     * @title_de --
     * @description_de --
     */
    exhibitorNewsList: ValidationSectionConfiguration
    /**
     * @title Exhibitor news validated fields' names
     * @description Validation configuration for the Exhibitor news section
     * @title_de --
     * @description_de --
     */
    storyNewsList: ValidationSectionConfiguration
    /**
     * @title Curated event dates validated fields' names
     * @description Validation configuration for the curated event dates section
     * @title_de --
     * @description_de --
     */
    curatedEventDates: ValidationSectionConfiguration
    /**
     * @title Non-curated event dates validated fields' names
     * @description Validation configuration for the non-curated event dates section
     * @title_de --
     * @description_de --
     */
    nonCuratedEventDates: ValidationSectionConfiguration
    /**
     * @title Showrooms validated fields' names
     * @description Validation configuration for the showrooms section
     * @title_de --
     * @description_de --
     */
    showrooms: ValidationSectionConfiguration
    /**
     * @title Coupons validated fields' names
     * @description Validation configuration for the coupons section
     * @title_de --
     * @description_de --
     */
    goodies: ValidationSectionConfiguration
    /**
     * @title Jobs validated fields' names
     * @description Validation configuration for the jobs section
     * @title_de --
     * @description_de --
     */
    jobOffers: ValidationSectionConfiguration
    /**
     * @title PrintCatalog validated fields' names
     * @description Validation configuration for the print catalog section
     * @title_de --
     * @description_de --
     */
    printCatalog: ValidationSectionConfiguration
    /**
     * @title NotificationTemplates validated fields' names
     * @description Validation configuration for the notification templates section
     * @title_de --
     * @description_de --
     */
    notificationTemplates: ValidationSectionConfiguration
    /**
     * @title NotificationTemplates - promotions validated fields' names
     * @description Validation configuration for the NotificationTemplates - promotions section
     * @title_de --
     * @description_de --
     */
    notificationPromotions: ValidationSectionConfiguration
}

export type LocalizedString = string & { __localizedBrand?: undefined }
export type ColorString = string & { __colorBrand?: undefined }

declare let branding: Branding
branding = new BrandingImpl(branding, "en")
const brandingImplCallable = branding as any

function getBranding(): Branding {
    return branding
}
export default getBranding()

export function setBrandingLanguage(lang: string) {
    brandingImplCallable.setLang(lang)
}
